import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, Trash2, Zap, ShoppingCart, Loader2, Radio, Barcode, 
  Check, Crown, X, Camera, ShoppingBag, ExternalLink, Key,
  Wallet, Bell, UtensilsCrossed, Target, TrendingDown, Lock, Heart, BookMarked,
  Share2, Copy, Link, RefreshCw, Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { PromoCodeModal, ProBadge } from "@/components/PromoCodeComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inject notification bell animations into document head (once)
if (typeof document !== 'undefined' && !document.getElementById('ps-notif-styles')) {
  const style = document.createElement('style');
  style.id = 'ps-notif-styles';
  style.textContent = `
    @keyframes ps-bell-shake {
      0%, 85%, 100% { transform: rotate(0deg); }
      88% { transform: rotate(-12deg); }
      91% { transform: rotate(10deg); }
      94% { transform: rotate(-8deg); }
      97% { transform: rotate(6deg); }
    }
    @keyframes ps-badge-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.15); }
    }
  `;
  document.head.appendChild(style);
}

// Generate or retrieve device ID — stored in 3 layers for maximum persistence:
// 1. Cookie (1-year expiry) — survives browser close, phone restart, most clears
// 2. localStorage — fast access, survives normal sessions
// 3. sessionStorage — fallback if both above are cleared mid-session
//
// Priority: cookie → localStorage → sessionStorage → generate new
// On every read, all three are synced so they stay consistent.

const DEVICE_ID_KEY = 'priceslice_device_id';
const COOKIE_EXPIRY_DAYS = 365;

function _getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function _setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

const getDeviceId = () => {
  // Try all three storage layers in order of persistence
  let deviceId = _getCookie(DEVICE_ID_KEY)
    || localStorage.getItem(DEVICE_ID_KEY)
    || sessionStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    // Generate a new ID — user is genuinely new or all storage was cleared
    deviceId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Sync to all three layers on every call so they stay in sync
  try { _setCookie(DEVICE_ID_KEY, deviceId, COOKIE_EXPIRY_DAYS); } catch(e) {}
  try { localStorage.setItem(DEVICE_ID_KEY, deviceId); } catch(e) {}
  try { sessionStorage.setItem(DEVICE_ID_KEY, deviceId); } catch(e) {}

  return deviceId;
};

// Create axios instance with device ID header
// Timeout is opt-in per call (via an optional config arg), not global — so
// existing calls like barcode-scan-triggered scraping (which can legitimately
// run long) keep their original no-timeout behaviour, untouched. Only calls
// that explicitly pass a timeout get one.
const apiClient = {
  get: (url, config = {}) => axios.get(url, { headers: { 'x-device-id': getDeviceId() }, ...config }),
  post: (url, data, config = {}) => axios.post(url, data, { headers: { 'x-device-id': getDeviceId() }, ...config }),
  patch: (url, data, config = {}) => axios.patch(url, data, { headers: { 'x-device-id': getDeviceId() }, ...config }),
  delete: (url, config = {}) => axios.delete(url, { headers: { 'x-device-id': getDeviceId() }, ...config }),
};

// Fuel loyalty scheme reference — static data, verified against each
// brand's own official page (16 Sep 2026), not scraped or auto-updated.
// The backend `loyalty_scheme_check` job (scheduler.py) watches these
// same official pages and emails an alert if anything changes, so this
// table can be manually reviewed and updated when that happens.
//
// Only brands verified against an official source are included. Morrisons,
// Asda, Texaco etc. aren't here yet — only secondary-source figures were
// found for those, not confirmed against an official page. Ask Claude to
// verify those the same way before adding them.
const LOYALTY_SCHEMES = {
  'shell': {
    scheme: 'Shell GO+ Rewards',
    rate: '~0.5p/litre back (200 pts = £1)',
    url: 'https://support.shell.com/hc/en-gb/articles/27217213965073-How-do-I-earn-points-in-Shell-GO-Rewards',
  },
  'bp': {
    scheme: 'BPme Rewards',
    rate: '~0.5p/litre back (200 pts = £1)',
    url: 'https://www.bp.com/en_gb/united-kingdom/home/products-and-services/bpme-rewards.html',
  },
  'tesco': {
    scheme: 'Tesco Clubcard',
    rate: '~0.5p/litre back (1 pt per 2L)',
    url: 'https://www.tesco.com/help/pages/clubcard-faqs/points/number-of-clubcard-points-you-can-collect-on-fuel',
  },
  'esso': {
    // Esso stations may ALSO take Tesco Clubcard if there's an on-site
    // Tesco Express, but that's not guaranteed — Nectar is the rate that
    // reliably applies at any Esso station, so that's what's shown here.
    scheme: 'Nectar',
    rate: '1 pt/litre, or 300 pts = 5p/litre off (min 10L fill)',
    url: 'https://www.nectar.com/esso',
  },
  "sainsbury's": {
    scheme: 'Nectar',
    rate: '~0.5p/litre back (500 pts = £2.50)',
    url: 'https://www.nectar.com/brands/sainsburys',
  },
  'sainsburys': {
    scheme: 'Nectar',
    rate: '~0.5p/litre back (500 pts = £2.50)',
    url: 'https://www.nectar.com/brands/sainsburys',
  },
};

function getLoyaltyScheme(brandOrRetailer) {
  if (!brandOrRetailer) return null;
  const key = brandOrRetailer.trim().toLowerCase();
  return LOYALTY_SCHEMES[key] || null;
}

// Store brand colors and affiliate config
const STORE_CONFIG = {
  'Tesco': { 
    bg: 'bg-tesco', 
    text: 'text-white',
    searchUrl: 'https://www.tesco.com/groceries/en-GB/search?query=',
    affiliateId: null // Tesco doesn't have grocery affiliates
  },
  'Sainsbury\'s': { 
    bg: 'bg-sainsburys', 
    text: 'text-white',
    searchUrl: 'https://www.sainsburys.co.uk/gol-ui/SearchResults/',
    affiliateId: null
  },
  'Sainsburys': { 
    bg: 'bg-sainsburys', 
    text: 'text-white',
    searchUrl: 'https://www.sainsburys.co.uk/gol-ui/SearchResults/',
    affiliateId: null
  },
  'ASDA': { 
    bg: 'bg-asda', 
    text: 'text-white',
    searchUrl: 'https://groceries.asda.com/search/',
    affiliateId: null
  },
  'Asda': { 
    bg: 'bg-asda', 
    text: 'text-white',
    searchUrl: 'https://groceries.asda.com/search/',
    affiliateId: null
  },
  'Morrisons': { 
    bg: 'bg-morrisons', 
    text: 'text-primary',
    searchUrl: 'https://groceries.morrisons.com/search?entry=',
    affiliateId: null
  },
  'Waitrose': { 
    bg: 'bg-[#5D8C51]', 
    text: 'text-white',
    searchUrl: 'https://www.waitrose.com/ecom/shop/search?searchTerm=',
    affiliateId: null
  },
  'Ocado': { 
    bg: 'bg-[#6F2C91]', 
    text: 'text-white',
    searchUrl: 'https://www.ocado.com/search?entry=',
    // Ocado is on Awin - add your Awin publisher ID
    affiliateId: null, // Set to your Awin ID when registered
    affiliateNetwork: 'awin',
    awinMerchantId: '2549'
  },
  'Iceland': { 
    bg: 'bg-[#E31837]', 
    text: 'text-white',
    searchUrl: 'https://www.iceland.co.uk/search?q=',
    // Iceland is on Awin
    affiliateId: null,
    affiliateNetwork: 'awin', 
    awinMerchantId: '5765'
  },
  'Co-op': { 
    bg: 'bg-[#00B1E7]', 
    text: 'text-white',
    searchUrl: 'https://www.coop.co.uk/search?query=',
    affiliateId: null
  },
  'Aldi': { 
    bg: 'bg-[#00457C]', 
    text: 'text-white',
    searchUrl: 'https://www.aldi.co.uk/search?q=',
    affiliateId: null
  },
  'ALDI': { 
    bg: 'bg-[#00457C]', 
    text: 'text-white',
    searchUrl: 'https://www.aldi.co.uk/search?q=',
    affiliateId: null
  },
  'Amazon': { 
    bg: 'bg-[#FF9900]', 
    text: 'text-primary',
    searchUrl: 'https://www.amazon.co.uk/s?k=',
    affiliateId: 'priceslice07-21',
    affiliateNetwork: 'amazon'
  },
  'Ebay': { 
    bg: 'bg-[#E53238]', 
    text: 'text-white',
    searchUrl: 'https://www.ebay.co.uk/sch/i.html?_nkw=',
    affiliateId: null
  },
};

// Generate affiliate link with tracking
const getAffiliateLink = (store, productName, barcode) => {
  const config = STORE_CONFIG[store] || {};
  const searchTerm = encodeURIComponent(productName || barcode || '');
  let url = (config.searchUrl || `https://www.google.com/search?q=${store}+`) + searchTerm;
  
  // Add affiliate tracking if configured
  if (config.affiliateId) {
    if (config.affiliateNetwork === 'amazon') {
      // Amazon Associates format
      url = `https://www.amazon.co.uk/s?k=${searchTerm}&tag=${config.affiliateId}`;
    } else if (config.affiliateNetwork === 'awin') {
      // Awin deep link format
      const encodedUrl = encodeURIComponent(url);
      url = `https://www.awin1.com/cread.php?awinmid=${config.awinMerchantId}&awinaffid=${config.affiliateId}&ued=${encodedUrl}`;
    }
  }
  
  return url;
};

// Track affiliate click
const trackAffiliateClick = async (store, itemName) => {
  try {
    await apiClient.post(`${API}/affiliate/click`, {
      store: store,
      item_name: itemName
    });
  } catch (error) {
    // Silent fail - don't interrupt user experience
    console.error('Affiliate tracking error:', error);
  }
};

// For backward compatibility
const STORE_COLORS = Object.fromEntries(
  Object.entries(STORE_CONFIG).map(([key, val]) => [key, { bg: val.bg, text: val.text }])
);

/**
 * Format the unit price for display alongside the pack price.
 * Normalises scraper unit strings and filters out obviously broken data.
 *
 * Examples:
 *   { price: 0.89, unit_price: 0.09, unit: "per 100g" } -> "£0.09/100g"
 *   { price: 2.50, unit_price: 0.0025, unit: "per g" }  -> "£0.25/100g"  (×100)
 *   { price: 1.20, unit_price: 1.20, unit: "per sngl" } -> "each"
 *   { price: 5.00, unit_price: 90.00, unit: "per kg" }  -> null  (impossibly high → skip)
 *   missing unit                                         -> null
 *
 * Returns null when we can't produce a trustworthy display string — caller
 * should just show the pack price on its own.
 */
const formatUnitPrice = (price) => {
  if (!price || typeof price.unit_price !== 'number') return null;
  const unit = (price.unit || '').toLowerCase().trim();
  if (!unit) return null;

  let up = price.unit_price;
  const pack = typeof price.price === 'number' ? price.price : 0;

  // Sanity check — reject obviously-broken scraper data.
  if (up <= 0) return null;

  // Some scrapers (notably Morrisons) return unit prices in PENCE not pounds.
  // Detect: if unit is countable (each, per roll, per egg) and unit_price > pack price,
  // a single item can't cost more than the whole pack — divide by 100 to fix pence→pounds.
  const isCountable = /each|sngl|roll|egg|wash|pouch|bag|sht|sheet|tab|pack|capsule/i.test(unit);
  if (isCountable && pack > 0 && up > pack) {
    up = up / 100;
    // If still nonsensical after pence fix, hide it
    if (up > pack) return null;
  }

  // General sanity: unit price > 50× pack price is almost certainly broken data
  if (pack > 0 && up > pack * 50) return null;

  // Normalise weight/volume units. Prefer "per 100g" and "per 100ml" as the
  // most user-friendly denominators for supermarket shopping.
  if (unit === 'per g') {
    return `£${(up * 100).toFixed(2)}/100g`;
  }
  if (unit === 'per ml') {
    return `£${(up * 100).toFixed(2)}/100ml`;
  }
  if (unit === 'per 100g') return `£${up.toFixed(2)}/100g`;
  if (unit === 'per 100ml') return `£${up.toFixed(2)}/100ml`;
  if (unit === 'per kg') return `£${up.toFixed(2)}/kg`;
  if (unit === 'per l' || unit === 'per litre') return `£${up.toFixed(2)}/litre`;
  if (unit === 'per cl') return `£${up.toFixed(2)}/cl`;

  // Per-item units — handle scraper artefacts and present cleanly.
  if (unit === 'each' || unit === 'per sngl') return `£${up.toFixed(2)} each`;
  if (unit === 'per egg') return `£${up.toFixed(2)}/egg`;
  if (unit === 'per roll') return `£${up.toFixed(2)}/roll`;
  if (unit === 'per wash') return `£${up.toFixed(2)}/wash`;
  if (unit === 'per pouch') return `£${up.toFixed(2)}/pouch`;
  if (unit === 'per bag') return `£${up.toFixed(2)}/bag`;
  if (/100\s*sht|100\s*sheet/i.test(unit)) return `£${up.toFixed(2)} per 100sht`;
  if (/sht|sheet/i.test(unit)) {
    // Per-sheet price should be tiny (fraction of a penny).
    // If > £0.05/sheet the scraper has returned a wrong value — hide it.
    if (up > 0.05) return null;
    return `£${up.toFixed(3)}/sheet`;
  }

  // Unknown unit string — show as-is
  return `£${up.toFixed(2)} ${unit}`;
};

function App() {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [priceResults, setPriceResults] = useState(() => {
    // Restore last comparison results from localStorage so a page refresh
    // or accidental browser close doesn't lose results (and waste a credit).
    // Results expire after 24 hours to avoid showing stale data.
    try {
      const saved = localStorage.getItem('priceslice_last_results');
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);
        if (ageHours < 24) return data;
        localStorage.removeItem('priceslice_last_results');
      }
    } catch { localStorage.removeItem('priceslice_last_results'); }
    return null;
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [usage, setUsage] = useState({ comparisons_used: 0, comparisons_limit: -1, items_limit: 5, is_pro: false, can_compare: true, can_add_items: true });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Phase C: Promo Codes State
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [proUntilDate, setProUntilDate] = useState(null);
  const [isProViaCode, setIsProViaCode] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [showPaygModal, setShowPaygModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [licenseKey, setLicenseKey] = useState("");
  const [showLicenseInput, setShowLicenseInput] = useState(false);
  const [activatingLicense, setActivatingLicense] = useState(false);
  const [showLicenseSuccess, setShowLicenseSuccess] = useState(null);
  const [isPro, setIsPro] = useState(false);
  // Budget & Meals state
  const [weeklyBudget, setWeeklyBudget] = useState(null);
  const [budgetInput, setBudgetInput] = useState("");
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showMealsModal, setShowMealsModal] = useState(false);
  const [meals, setMeals] = useState([]);
  const [mealCategories, setMealCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [loadingMealCategories, setLoadingMealCategories] = useState(false);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [showSavedRecipesModal, setShowSavedRecipesModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  // Notification centre
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  const [alertsMeta, setAlertsMeta] = useState({
    can_add_grocery: true, can_add_fuel: false,
    is_paid: false,
    max_grocery: 1, max_fuel: 0,
    grocery_count: 0, fuel_count: 0,
  });

  const [showAlertsModal, setShowAlertsModal] = useState(false);
  // Grocery alert fields
  const [alertItemName, setAlertItemName] = useState("");
  const [isSubmittingAlert, setIsSubmittingAlert] = useState(false);
  const [alertTargetPrice, setAlertTargetPrice] = useState("");
  const [alertStoreName, setAlertStoreName] = useState("");
  // Fuel alert fields
  const [alertType, setAlertType] = useState("grocery");
  const [alertFuelType, setAlertFuelType] = useState("petrol");
  const [alertFuelPostcode, setAlertFuelPostcode] = useState("");
  const [alertFuelRadius, setAlertFuelRadius] = useState(5);
  const [alertFuelTargetPence, setAlertFuelTargetPence] = useState("");
  const [priceFreshness, setPriceFreshness] = useState(null);
  // Fuel prices state
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [fuelData, setFuelData] = useState(null);
  const [loadingFuel, setLoadingFuel] = useState(false);
  const [fuelLoadingStep, setFuelLoadingStep] = useState("📍 Getting your location...");
  const [userLocation, setUserLocation] = useState(null);
  const [locationTimestamp, setLocationTimestamp] = useState(null);
  const [fuelRadius, setFuelRadius] = useState(5);
  const [postcodeInput, setPostcodeInput] = useState("");
  const [postcodeError, setPostcodeError] = useState("");
  const [usingPostcode, setUsingPostcode] = useState(false);
  const [geolocationFailed, setGeolocationFailed] = useState(false);
  const [activeFuelFilters, setActiveFuelFilters] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const notificationsInFlightRef = useRef(false);
  const notificationsFailCountRef = useRef(0);
  const notificationsBackoffUntilRef = useRef(0);
  const suggestionItemRefs = useRef([]);
  const mealsListRef = useRef(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Fetch price freshness info
  const fetchPriceFreshness = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API}/prices/freshness`);
      setPriceFreshness(response.data);
    } catch (error) {
      console.error("Error fetching price freshness:", error);
    }
  }, []);

  // Fetch shopping list and usage on mount
  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, usageRes, budgetRes] = await Promise.all([
        apiClient.get(`${API}/shopping-list`),
        apiClient.get(`${API}/usage`),
        apiClient.get(`${API}/budget`)
      ]);
      setItems(itemsRes.data);
      setUsage(usageRes.data);
      setIsPro(usageRes.data.is_pro || false);
      if (budgetRes.data.weekly_budget) {
        setWeeklyBudget(budgetRes.data.weekly_budget);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchSavedRecipes();
    fetchPriceFreshness();
    fetchNotifications();

    // Poll for new notifications every 30 seconds
    // Lightweight — just checks unread count, no heavy DB queries
    const notifInterval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(notifInterval);
    
    // Check for payment success
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (sessionId && success) {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData]);

  // Persist comparison results to localStorage so they survive page refresh
  // AND browser close. Timestamped so stale results auto-expire after 24h.
  useEffect(() => {
    try {
      if (priceResults) {
        localStorage.setItem('priceslice_last_results', JSON.stringify({
          data: priceResults,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem('priceslice_last_results');
      }
    } catch { /* storage full or unavailable — ignore */ }
  }, [priceResults]);

  // Phase C: Load pro status from promo codes
  useEffect(() => {
    const loadProStatus = async () => {
      try {
        const response = await apiClient.get(`${API}/usage`);
        const data = response.data;

        // Set pro_until date for display
        if (data.pro_until) {
          setProUntilDate(data.pro_until);
        }

        // isProViaCode = has pro_until BUT no Stripe subscription
        // Paid Stripe subscribers also have pro_until but are NOT promo code users
        if (data.pro_until && !data.is_stripe_subscriber) {
          const proUntil = new Date(data.pro_until);
          if (proUntil > new Date()) {
            setIsProViaCode(true);
          }
        } else {
          setIsProViaCode(false);
        }
      } catch (err) {
        console.error('Error loading pro status:', err);
      }
    };
    
    const deviceId = getDeviceId();
    if (deviceId) {
      loadProStatus();
    }
  }, []);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }
    
    setCheckingPayment(true);
    
    try {
      const response = await apiClient.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        const key = response.data.license_key;
        if (key) {
          setShowLicenseSuccess(key);
          toast.success("Payment successful! Your license key is ready.");
        } else {
          toast.success("Payment successful! Welcome to Pro!");
        }
        await fetchData();
        setCheckingPayment(false);
        setShowUpgradeModal(false);
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
    } catch (error) {
      console.error("Error checking payment:", error);
      setCheckingPayment(false);
    }
  };

  // Phase C: Handle promo code success
  const handlePromoSuccess = (data) => {
    setProUntilDate(data.pro_until);
    setIsProViaCode(true);
    
    // Show success toast
    toast.success(`✓ ${data.message}`);
    
    // Close modals
    setShowUpgradeModal(false);
    setIsPromoModalOpen(false);
    
    // Refresh usage data
    fetchData();
  };

  // Activate license key
  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }
    
    setActivatingLicense(true);
    
    try {
      const response = await apiClient.post(`${API}/license/activate`, {
        license_key: licenseKey.trim()
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowUpgradeModal(false);
        setShowLicenseInput(false);
        setLicenseKey("");
        await fetchData();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to activate license";
      toast.error(message);
    } finally {
      setActivatingLicense(false);
    }
  };

  // Budget handlers
  const handleSaveBudget = async () => {
    const budget = parseFloat(budgetInput);
    if (isNaN(budget) || budget <= 0) {
      toast.error("Please enter a valid budget");
      return;
    }
    
    try {
      await apiClient.post(`${API}/budget`, { weekly_budget: budget });
      setWeeklyBudget(budget);
      setShowBudgetModal(false);
      toast.success(`Budget set to £${budget.toFixed(2)}`);
    } catch (error) {
      toast.error("Failed to save budget");
    }
  };

  // Meal suggestions handlers
  const fetchMeals = async (category = null) => {
    setLoadingMeals(true);
    try {
      let url = `${API}/meal-suggestions`;
      const params = new URLSearchParams();
      if (weeklyBudget) params.append('budget', weeklyBudget);
      if (category) params.append('category', category);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setMeals(response.data.meals);
    } catch (error) {
      toast.error("Failed to load meal suggestions");
    } finally {
      setLoadingMeals(false);
    }
  };

  const fetchMealCategories = async () => {
    setLoadingMealCategories(true);
    try {
      const response = await apiClient.get(`${API}/meal-categories`);
      setMealCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching meal categories:", error);
    } finally {
      setLoadingMealCategories(false);
    }
  };

  const handleAddMealToList = async (mealName) => {
    try {
      const response = await apiClient.post(`${API}/meal-to-list?meal_name=${encodeURIComponent(mealName)}`);
      toast.success(response.data.message);
      await fetchData();
      setShowMealsModal(false);
    } catch (error) {
      toast.error("Failed to add ingredients");
    }
  };

  // Saved recipes handlers
  const fetchSavedRecipes = async () => {
    try {
      const response = await apiClient.get(`${API}/saved-recipes`);
      setSavedRecipes(response.data.recipes);
    } catch (error) {
      console.error("Error fetching saved recipes:", error);
    }
  };

  const handleSaveRecipe = async (recipeName) => {
    try {
      await apiClient.post(`${API}/saved-recipes?recipe_name=${encodeURIComponent(recipeName)}`);
      toast.success("Recipe saved to favourites!");
      await fetchSavedRecipes();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Recipe already saved");
      } else {
        toast.error("Failed to save recipe");
      }
    }
  };

  const handleRemoveSavedRecipe = async (recipeId) => {
    try {
      await apiClient.delete(`${API}/saved-recipes/${encodeURIComponent(recipeId)}`);
      toast.success("Recipe removed");
      await fetchSavedRecipes();
    } catch (error) {
      toast.error("Failed to remove recipe");
    }
  };

  const isRecipeSaved = (recipeName) => {
    if (!recipeName) return false;
    return savedRecipes.some(r => r?.name?.toLowerCase() === recipeName.toLowerCase());
  };

  // Share list handlers
  const handleCreateShareLink = async () => {
    setCreatingShareLink(true);
    try {
      const response = await apiClient.post(`${API}/share-list`);
      const fullUrl = `${window.location.origin}/shared/${response.data.share_id}`;
      setShareLink(fullUrl);
      toast.success("Share link created!");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Add items to your list first");
      } else {
        toast.error("Failed to create share link");
      }
    } finally {
      setCreatingShareLink(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      toast.success("Link copied to clipboard!");
    }
  };

  // Price alert handlers
  const fetchAlerts = async () => {
    try {
      const response = await apiClient.get(`${API}/alerts`);
      if (Array.isArray(response.data)) {
        setPriceAlerts(response.data);
      } else {
        setPriceAlerts(response.data.alerts || []);
        setAlertsMeta({
          can_add_grocery: response.data.can_add_grocery ?? true,
          can_add_fuel:    response.data.can_add_fuel ?? false,
          is_paid:         response.data.is_paid ?? false,
          max_grocery:     response.data.max_grocery ?? 1,
          max_fuel:        response.data.max_fuel ?? 0,
          grocery_count:   response.data.grocery_count ?? 0,
          fuel_count:      response.data.fuel_count ?? 0,
        });
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const handleCreateAlert = async () => {
    // Guard against double-submit: rapid repeated taps (impatience, slow
    // network, or re-pressing after a re-render) used to each fire a
    // separate request, creating duplicate alerts for the same item before
    // the first request even completed. The backend now also rejects exact
    // duplicates server-side (belt-and-suspenders), but blocking re-entry
    // here stops the wasted requests from firing at all.
    if (isSubmittingAlert) return;

    if (alertType === "grocery") {
      if (!alertItemName.trim()) { toast.error("Please enter an item name"); return; }
      // target_price is optional for grocery alerts — leaving it blank means
      // "alert me if the price drops at all from today's cheapest" (baseline
      // mode, set server-side). Only validate if the user actually typed
      // something, so we still catch "0", "-5", or non-numeric junk.
      if (alertTargetPrice.trim() && parseFloat(alertTargetPrice) <= 0) {
        toast.error("Target price must be greater than zero, or leave it blank to alert on any price drop");
        return;
      }
    } else {
      if (!alertFuelPostcode.trim()) { toast.error("Please enter your postcode"); return; }
      if (!alertFuelTargetPence || parseInt(alertFuelTargetPence) <= 0) { toast.error("Please enter target price in pence (e.g. 135)"); return; }
    }

    setIsSubmittingAlert(true);
    try {
      const payload = alertType === "grocery"
        ? {
            alert_type:   "grocery",
            item_name:    alertItemName.trim(),
            // Blank input = baseline mode (alert on any future drop from
            // today's cheapest price) — backend handles target_price=null
            // by capturing today's price as the baseline. Explicitly send
            // null rather than relying on parseFloat('') => NaN to survive
            // JSON serialization correctly.
            target_price: alertTargetPrice.trim() ? parseFloat(alertTargetPrice) : null,
            store_name:   alertStoreName || null,
          }
        : {
            alert_type:        "fuel",
            item_name:         `${alertFuelType} near ${alertFuelPostcode.toUpperCase()}`,
            target_price:      parseInt(alertFuelTargetPence) / 100,
            fuel_type:         alertFuelType,
            fuel_postcode:     alertFuelPostcode.toUpperCase().trim(),
            fuel_radius_miles: alertFuelRadius,
          };

      await apiClient.post(`${API}/alerts`, payload);
      toast.success(alertType === "grocery" ? "Grocery alert created! ✓" : "Fuel alert created! ⛽");
      setAlertItemName(""); setAlertTargetPrice(""); setAlertStoreName("");
      setAlertFuelPostcode(""); setAlertFuelTargetPence("");
      setAlertType("grocery");
      await fetchAlerts();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(error.response.data?.detail || "Upgrade to Alerts (£2.99/mo) to add more alerts");
        setShowAlertsModal(false);
        setShowUpgradeModal(true);
      } else if (error.response?.status === 409) {
        // Duplicate alert — backend already has an active one for this item
        toast.error(error.response.data?.detail || "You already have an active alert for this item");
      } else {
        toast.error("Failed to create alert. Please try again.");
      }
    } finally {
      setIsSubmittingAlert(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await apiClient.delete(`${API}/alerts/${alertId}`);
      toast.success("Alert deleted");
      await fetchAlerts();
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  const fetchNotifications = async () => {
    // Guard against overlapping polls: if the previous call is still
    // pending (e.g. backend is slow), skip this tick rather than firing
    // another request on top of it. Without this, a single hung request
    // plus the 30s poll interval piles up connections indefinitely and
    // can exhaust the browser's per-host connection limit
    // (ERR_INSUFFICIENT_RESOURCES) — which then blocks other requests to
    // the same API host too, fuel search included.
    if (notificationsInFlightRef.current) {
      return;
    }
    // After repeated failures, space out retries instead of hammering a
    // struggling backend every 30s — but keep retrying (with a 5 min cap),
    // never stop permanently, so it recovers on its own once the backend
    // is healthy again.
    if (Date.now() < notificationsBackoffUntilRef.current) {
      return;
    }
    notificationsInFlightRef.current = true;
    try {
      const response = await apiClient.get(`${API}/notifications`, { timeout: 15000 });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      notificationsFailCountRef.current = 0;
      notificationsBackoffUntilRef.current = 0;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      notificationsFailCountRef.current += 1;
      const backoffMs = Math.min(30000 * 2 ** notificationsFailCountRef.current, 5 * 60000);
      notificationsBackoffUntilRef.current = Date.now() + backoffMs;
    } finally {
      notificationsInFlightRef.current = false;
    }
  };

  const markNotificationsRead = async () => {
    try {
      await apiClient.post(`${API}/notifications/mark-read`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking notifications read:", error);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await apiClient.delete(`${API}/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Fetch suggestions as user types
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (newItem.length < 2) {
        setSuggestions([]);
        return;
      }
      
      try {
        const response = await apiClient.get(`${API}/products/suggest?q=${encodeURIComponent(newItem)}`);
        suggestionItemRefs.current = [];
        setSuggestions(response.data);
        setShowSuggestions(response.data.length > 0);
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };
    
    const debounce = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(debounce);
  }, [newItem]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted suggestion into view when arrow keys move selection
  useEffect(() => {
    if (selectedSuggestionIndex < 0) return;
    const el = suggestionItemRefs.current[selectedSuggestionIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedSuggestionIndex]);

  // Barcode Scanner
  const startScanner = useCallback(async () => {
    if (!scannerRef.current) return;
    
    try {
      const html5QrCode = new Html5Qrcode("barcode-scanner");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 100 },
          aspectRatio: 1.0
        },
        async (decodedText) => {
          // Barcode detected
          await stopScanner();
          setShowScannerModal(false);

          // If scanning for an alert, resolve product name and populate alert field
          if (window._alertScanCallback) {
            try {
              const res = await apiClient.get(`${API}/resolve-barcode/${decodedText}`);
              const productName = res.data?.name || `Product (${decodedText})`;
              window._alertScanCallback(productName);
              window._alertScanCallback = null;
              toast.success(`Found: ${productName}`);
            } catch {
              // Fallback: use barcode as search term
              window._alertScanCallback(`barcode:${decodedText}`);
              window._alertScanCallback = null;
            }
            return;
          }

          // Default: Add item with barcode to shopping list
          try {
            const payload = {
              name: `Product (${decodedText})`,
              barcode: decodedText,
              quantity: 1
            };
            const response = await apiClient.post(`${API}/shopping-list`, payload);
            setItems(prev => [response.data, ...prev]);
            setPriceResults(null);
            toast.success(`Added product with barcode ${decodedText}`);
          } catch (error) {
            toast.error("Failed to add scanned item");
          }
        },
        () => {} // Error callback - ignore
      );
    } catch (err) {
      console.error("Scanner error:", err);
      toast.error("Could not access camera. Please check permissions.");
      setShowScannerModal(false);
    }
  }, []);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (showScannerModal) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => startScanner(), 100);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
    }
  }, [showScannerModal, startScanner, stopScanner]);

  // Add item to list
  const handleAddItem = async (e, selectedSuggestion = null) => {
    e?.preventDefault();
    
    const itemToAdd = selectedSuggestion || { name: newItem.trim(), barcode: null };
    if (!itemToAdd.name) return;

    // Defect 5 fix: warn if item already exists in basket (prevents wasting credits)
    const nameToAdd = itemToAdd.name.trim().toLowerCase();
    const duplicate = items.find(i => i.name?.trim().toLowerCase() === nameToAdd);
    if (duplicate) {
      toast.error(`"${itemToAdd.name}" is already in your list`);
      setNewItem("");
      return;
    }

    setLoading(true);
    setShowSuggestions(false);
    
    try {
      const payload = {
        name: itemToAdd.name,
        barcode: itemToAdd.barcode,
        quantity: 1
      };
      
      const response = await apiClient.post(`${API}/shopping-list`, payload);
      setItems([response.data, ...items]);
      setNewItem("");
      setPriceResults(null);
      toast.success(`Added "${itemToAdd.name}" to list`);
    } catch (error) {
      console.error("Error adding item:", error);
      if (error.response?.status === 402) {
        setShowUpgradeModal(true);
        // Backend returns the user's effective limit in usage.items_limit
        // (10 for free, 50 for paid). 402 here means they hit it.
        if (usage.is_pro || usage.is_payg) {
          toast.error(`You've reached the ${usage.items_limit}-item limit per list. Remove items to add more.`);
        } else {
          toast.error(`Free lists hold up to ${usage.items_limit} items. Upgrade to Alerts (£2.99/mo) for 50 items + full split-shop breakdown.`);
        }
      } else {
        toast.error("Failed to add item");
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle item completed (strikethrough)
  const handleToggleCompleted = async (item) => {
    try {
      const response = await apiClient.patch(`${API}/shopping-list/${item.id}`, {
        completed: !item.completed
      });
      setItems(items.map(i => i.id === item.id ? response.data : i));
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  // Remove item from list
  const handleRemoveItem = async (itemId, itemName) => {
    try {
      await apiClient.delete(`${API}/shopping-list/${itemId}`);
      setItems(items.filter(item => item.id !== itemId));
      setPriceResults(null);
      toast.success(`Removed "${itemName}"`);
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item");
    }
  };

  // Compare prices
  const handleComparePrices = async () => {
    const activeItems = items.filter(i => !i.completed);
    
    if (activeItems.length === 0) {
      toast.error("Add some items to compare prices");
      return;
    }

    if (!usage.can_compare) {
      setShowUpgradeModal(true);
      return;
    }

    setComparing(true);
    setPriceResults(null);
    
    try {
      const response = await apiClient.post(`${API}/compare-prices`);
      setPriceResults(response.data);
      
      const usageRes = await apiClient.get(`${API}/usage`);
      setUsage(usageRes.data);
      
      const successCount = response.data.item_results?.filter(r => r.prices.length > 0).length || 0;
      if (successCount > 0) {
        toast.success(`Found prices for ${successCount}/${response.data.item_results?.length || 0} items`);
      } else {
        toast.warning("Not in our database yet — tap \"Notify me when found\" below to get alerted");
      }
    } catch (error) {
      console.error("Error comparing prices:", error);
      if (error.response?.status === 402) {
        setShowUpgradeModal(true);
        toast.error("Your basket has too many items for the free tier. Upgrade to Alerts (£2.99/mo) for up to 50 items.");
      } else {
        toast.error("Failed to compare prices. Please try again.");
      }
    } finally {
      setComparing(false);
    }
  };

  // Handle upgrade
  const handleUpgrade = async (packageId) => {
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    }
  };

  // Get user location and fetch fuel prices
  const calcDistanceMiles = (lat1, lng1, lat2, lng2) => {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1);
  };

  // Look up a UK postcode and get its lat/lng using the free postcodes.io API
  const lookupPostcode = async (postcode) => {
    const cleanPostcode = postcode.trim().toUpperCase().replace(/\s+/g, '');
    if (!cleanPostcode) {
      throw new Error('Please enter a postcode');
    }
    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleanPostcode)}`);
      const data = await response.json();
      if (data.status !== 200 || !data.result) {
        throw new Error('Postcode not found');
      }
      return {
        lat: data.result.latitude,
        lng: data.result.longitude,
        postcode: data.result.postcode,
      };
    } catch (err) {
      if (err.message === 'Postcode not found' || err.message === 'Please enter a postcode') {
        throw err;
      }
      throw new Error('Could not look up postcode. Please check your internet connection.');
    }
  };

  const handleFuelPrices = async (manualPostcode = null) => {
    setShowFuelModal(true);
    setLoadingFuel(true);
    setFuelData(null);
    setPostcodeError("");

    // Defensive: only treat manualPostcode as postcode if it's a non-empty string
    // (Otherwise it might be a click event object passed accidentally by onClick handlers)
    const postcodeToUse = (typeof manualPostcode === 'string' && manualPostcode.trim()) 
      ? manualPostcode 
      : null;

    try {
      let latitude, longitude;

      if (postcodeToUse) {
        // User entered a postcode manually
        setFuelLoadingStep(`📍 Looking up ${postcodeToUse}...`);
        const result = await lookupPostcode(postcodeToUse);
        latitude = result.lat;
        longitude = result.lng;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationTimestamp(Date.now());
        setUsingPostcode(true);
        setGeolocationFailed(false);
        setFuelLoadingStep("⛽ Finding cheapest fuel near you...");
      } else {
        // Use cached location if available and less than 30 minutes old
        const locationAge = locationTimestamp ? (Date.now() - locationTimestamp) / 60000 : 999;
        if (userLocation && locationAge < 30) {
          latitude = userLocation.lat;
          longitude = userLocation.lng;
          setFuelLoadingStep("⛽ Finding cheapest fuel near you...");
        } else {
          // Try browser geolocation
          setFuelLoadingStep("📍 Getting your location...");
          try {
            const position = await new Promise((resolve, reject) => {
              if (!navigator.geolocation) {
                reject({ code: 2, message: 'Geolocation not supported' });
                return;
              }
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 5000,
                enableHighAccuracy: false  // Use WiFi/cell (instant) not GPS (3-5s)
                // High accuracy GPS is unnecessary for fuel station search — 
                // WiFi/cell location is accurate to 50-500m which is fine for
                // finding stations within a 5 mile radius
              });
            });
            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            setUserLocation({ lat: latitude, lng: longitude });
            setLocationTimestamp(Date.now());
            setUsingPostcode(false);
            setGeolocationFailed(false);
            setFuelLoadingStep("⛽ Finding cheapest fuel near you...");
          } catch (geoError) {
            // Geolocation failed - show postcode input UI but keep modal open
            console.error('Geolocation error:', geoError);
            setGeolocationFailed(true);
            setLoadingFuel(false);
            if (geoError.code === 1) {
              setPostcodeError("Location access denied. Please enter your postcode below.");
            } else if (geoError.code === 3) {
              setPostcodeError("Location request timed out. Please enter your postcode below.");
            } else {
              setPostcodeError("Couldn't detect your location. Please enter your postcode below.");
            }
            return; // Exit early - don't try to fetch fuel without coords
          }
        }
      }

      // Now fetch the fuel data
      const response = await apiClient.get(
        `${API}/fuel/nearby?lat=${latitude}&lng=${longitude}&radius=${fuelRadius}`,
        { timeout: 15000 }
      );
      setFuelData(response.data);

    } catch (error) {
      console.error('Fuel fetch error:', error);
      if (error.message === 'Postcode not found') {
        setPostcodeError("Postcode not found. Please check and try again.");
      } else if (error.message === 'Please enter a postcode') {
        setPostcodeError("Please enter a postcode.");
      } else if (error.message?.includes('Could not look up postcode')) {
        setPostcodeError(error.message);
      } else if (error.response?.status === 403) {
        // Step 1: Fuel search is now free — 403 should never fire from backend
        // If it does (e.g. old cached response), show a plain error not upgrade modal
        toast.error("Unable to load fuel prices. Please try again.");
      } else {
        // Network error or backend error
        const errMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Could not fetch fuel prices: ${errMsg}`);
      }
    } finally {
      setLoadingFuel(false);
    }
  };

  const handlePostcodeSubmit = (e) => {
    e?.preventDefault();
    if (!postcodeInput.trim()) {
      setPostcodeError("Please enter a postcode.");
      return;
    }
    handleFuelPrices(postcodeInput);
  };

  const getFuelTypeColor = (retailer) => {
    const colors = {
      'Tesco': 'bg-blue-100 text-blue-800',
      "Sainsbury's": 'bg-orange-100 text-orange-800',
      'Asda': 'bg-green-100 text-green-800',
      'Morrisons': 'bg-yellow-100 text-yellow-800',
      'BP': 'bg-green-100 text-green-800',
      'Esso': 'bg-red-100 text-red-800',
      'Shell': 'bg-yellow-100 text-yellow-800',
      'JET': 'bg-red-100 text-red-800',
    };
    return colors[retailer] || 'bg-gray-100 text-gray-800';
  };

  const getStoreStyle = (store) => {
    return STORE_COLORS[store] || { bg: 'bg-primary', text: 'text-white' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return '';
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}`;
  };

  const activeItemsCount = items.filter(i => !i.completed).length;
  const completedItemsCount = items.filter(i => i.completed).length;

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-center" richColors />
      
      <div className="receipt-container">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary rounded-xl">
                <ShoppingCart className="w-6 h-6 text-white" />
              </div>
              <h1 className="font-syne text-3xl font-extrabold text-primary tracking-tight">
                PriceSlice
              </h1>
            </div>
            <div className="flex flex-col items-end gap-1">
              {/* Usage Badge - Updated with Phase C promo codes */}
              {/* Notification bell — always visible, animated when unread */}
              <button
                onClick={() => {
                  setShowNotifPanel(v => !v);
                  if (unreadCount > 0) markNotificationsRead();
                }}
                className="relative p-1.5 rounded-full transition-colors hover:bg-muted"
                aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
                style={{
                  animation: unreadCount > 0 ? 'ps-bell-shake 2s ease-in-out infinite' : 'none',
                }}
              >
                <Bell
                  className="w-5 h-5 transition-colors"
                  style={{ color: unreadCount > 0 ? '#16a34a' : 'var(--color-text-secondary)' }}
                />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex items-center justify-center font-bold"
                    style={{
                      minWidth: '18px', height: '18px',
                      fontSize: '9px', borderRadius: '9999px',
                      background: '#ef4444', color: '#fff',
                      padding: '0 3px',
                      boxShadow: '0 0 0 2px white',
                      animation: 'ps-badge-pulse 2s ease-in-out infinite',
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {usage.is_pro ? (
                <Badge
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-1 cursor-pointer"
                  onClick={() => setShowManageModal(true)}
                >
                  <Crown className="w-3 h-3" />
                  Pro
                </Badge>
              ) : (
                <>
                  <ProBadge
                    isProViaPaid={false}
                    isProViaCode={isProViaCode}
                    proUntilDate={proUntilDate}
                    searchesLeft={usage.is_payg ? `${usage.comparisons_remaining} credits` : null}
                  />
                  {!isProViaCode && !usage.is_pro && (
                    <Button
                      onClick={() => setShowUpgradeModal(true)}
                      className="h-7 text-xs px-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Upgrade
                    </Button>
                  )}
                </>
              )}
              {/* Price Freshness Status Badge */}
              {priceFreshness && priceFreshness.freshness_status === 'fresh' && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                  <Radio className="w-3 h-3 animate-pulse" />
                  Updated
                </Badge>
              )}
              {priceFreshness && priceFreshness.freshness_status === 'stale' && (
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs flex items-center gap-1">
                  <Radio className="w-3 h-3" />
                  {Math.round(priceFreshness.hours_since_update)}h ago
                </Badge>
              )}
              {(!priceFreshness || priceFreshness.freshness_status === 'no_live_data' || priceFreshness.freshness_status === 'outdated') && (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs flex items-center gap-1">
                  <Radio className="w-3 h-3" />
                  Est.
                </Badge>
              )}
            </div>
          </div>
          <p className="text-muted-foreground font-manrope text-sm">
            UK supermarket price comparison
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Prices are indicative only and may differ in-store. <a href="/terms" className="underline hover:text-primary">Terms</a> · <a href="/privacy" className="underline hover:text-primary">Privacy</a> · <a href="/contact" className="underline hover:text-primary">Contact</a>
          </p>
        </header>

        {/* Fuel Prices Button - 1 free/month, unlimited for Pro */}
        <div className="px-6 pt-3 pb-0">
          <Button
            onClick={handleFuelPrices}
            variant="outline"
            className="w-full h-11 rounded-2xl font-syne font-bold border-2 border-orange-300 text-orange-600 hover:bg-orange-100 hover:text-orange-700 transition-all"
          >
            ⛽ Find Cheapest Fuel Near Me
          </Button>
        </div>

        {/* Notification panel — fixed overlay so it's always visible regardless of scroll position */}
        {showNotifPanel && (
          <>
            {/* Backdrop — tap outside to dismiss */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowNotifPanel(false)}
            />
            <div
              className="fixed z-50 overflow-hidden bg-background"
              style={{
                top: '60px',
                right: '12px',
                left: '12px',
                maxWidth: '420px',
                marginLeft: 'auto',
                border: '1.5px solid #16a34a',
                borderRadius: '14px',
                boxShadow: '0 8px 32px rgba(22,163,74,0.18)',
              }}>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3"
              style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderBottom: '1px solid #bbf7d0' }}>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-full"
                  style={{ width: 28, height: 28, background: '#16a34a' }}>
                  <Bell className="w-3.5 h-3.5" style={{ color: 'white' }} />
                </div>
                <div>
                  <span className="font-syne font-bold text-sm" style={{ color: '#14532d' }}>Price Alerts</span>
                  {unreadCount > 0 && (
                    <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{ background: '#ef4444', color: 'white', fontSize: '10px' }}>
                      {unreadCount} new
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {notifications.some(n => !n.is_read) && (
                  <button onClick={markNotificationsRead}
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#16a34a' }}>
                    Mark all read
                  </button>
                )}
                <button onClick={() => setShowNotifPanel(false)}
                  className="flex items-center justify-center rounded-full hover:bg-white/60 transition-colors"
                  style={{ width: 24, height: 24, color: '#6b7280', fontSize: '18px', lineHeight: 1 }}>
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="flex items-center justify-center rounded-full mx-auto mb-3"
                  style={{ width: 48, height: 48, background: '#f0fdf4' }}>
                  <Bell className="w-6 h-6" style={{ color: '#86efac' }} />
                </div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>No notifications yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Set a price alert — we'll notify you here when prices drop
                </p>
                <button
                  onClick={() => { setShowNotifPanel(false); setShowAlertsModal(true); fetchAlerts(); }}
                  className="mt-3 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  style={{ background: '#16a34a', color: 'white' }}>
                  Set up alerts
                </button>
              </div>
            ) : (
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {notifications.map((notif, i) => (
                  <div key={notif.id}
                    className="flex items-start gap-3 px-4 py-3"
                    style={{
                      borderBottom: i < notifications.length - 1 ? '1px solid #f0fdf4' : 'none',
                      background: !notif.is_read ? '#f0fdf4' : 'transparent',
                    }}>
                    {/* Icon */}
                    <div className="flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                      style={{ width: 32, height: 32, background: notif.alert_type === 'fuel' ? '#fff7ed' : '#f0fdf4',
                               border: `1.5px solid ${notif.alert_type === 'fuel' ? '#fed7aa' : '#bbf7d0'}` }}>
                      <span style={{ fontSize: '14px' }}>{notif.alert_type === 'fuel' ? '⛽' : '🛒'}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug"
                        style={{ color: !notif.is_read ? '#14532d' : 'var(--color-text-primary)' }}>
                        {notif.title}
                      </p>
                      <p className="text-xs mt-0.5 leading-snug" style={{ color: 'var(--color-text-secondary)' }}>
                        {notif.message}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--color-text-tertiary)', fontSize: '10px' }}>
                        {new Date(notif.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                        {!notif.is_read && (
                          <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full align-middle"
                            style={{ background: '#16a34a' }} />
                        )}
                      </p>
                    </div>

                    {/* Dismiss */}
                    <button onClick={() => deleteNotification(notif.id)}
                      className="flex-shrink-0 p-1 rounded-full hover:bg-red-50 transition-colors"
                      style={{ color: '#d1d5db' }}
                      onMouseOver={e => e.currentTarget.style.color='#ef4444'}
                      onMouseOut={e => e.currentTarget.style.color='#d1d5db'}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
        )}

        {/* Add Item Form */}
        <div className="p-6 border-b border-border">
          <form onSubmit={handleAddItem} className="flex gap-2 relative" data-testid="add-item-form">
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (suggestions.length === 0) return;
                  // Allow ArrowDown to re-open a closed dropdown
                  if (!showSuggestions) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex(0);
                    }
                    return;
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedSuggestionIndex(i => (i + 1) % suggestions.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedSuggestionIndex(i => i <= 0 ? suggestions.length - 1 : i - 1);
                  } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
                    e.preventDefault();
                    handleAddItem(e, suggestions[selectedSuggestionIndex]);
                    setSelectedSuggestionIndex(-1);
                  } else if (e.key === 'Escape') {
                    setShowSuggestions(false);
                    setSelectedSuggestionIndex(-1);
                  }
                }}
                placeholder="Enter product name or barcode..."
                className="font-manrope text-base h-12 border-2 border-border focus:border-primary transition-colors pr-10"
                data-testid="item-input"
                disabled={loading}
                autoComplete="off"
              />
              <Barcode className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/50" />
              
              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                  data-testid="suggestions-dropdown"
                >
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      ref={(el) => { suggestionItemRefs.current[index] = el; }}
                      type="button"
                      onClick={(e) => handleAddItem(e, suggestion)}
                      className={`w-full px-4 py-3 text-left flex items-center justify-between border-b border-border last:border-0 transition-colors ${index === selectedSuggestionIndex ? "bg-secondary" : "hover:bg-secondary/50"}`}
                      data-testid={`suggestion-${index}`}
                    >
                      <span className="font-manrope font-medium text-primary">{suggestion.name}</span>
                      <span className="text-xs text-muted-foreground font-space">{suggestion.barcode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Scan Button */}
            <Button 
              type="button"
              variant="outline"
              onClick={() => setShowScannerModal(true)}
              className="h-12 px-3 border-2"
              data-testid="scan-btn"
            >
              <Camera className="w-5 h-5" />
            </Button>
            
            <Button 
              type="submit" 
              disabled={loading || !newItem.trim()}
              className="h-12 px-4 bg-primary hover:bg-primary/90 transition-all"
              data-testid="add-item-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            Start typing for suggestions, or tap <Camera className="w-3 h-3 inline" /> to scan a barcode
            <span className="block text-[10px] opacity-70 mt-1">
              (Camera permission required for barcode scanning)
            </span>
          </p>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBudgetInput(weeklyBudget?.toString() || "");
                setShowBudgetModal(true);
              }}
              className="text-xs"
              data-testid="budget-btn"
            >
              <Wallet className="w-3 h-3 mr-1" />
              {weeklyBudget ? `Budget: £${weeklyBudget}` : "Set Budget"}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedCategory(null);
                setShowMealsModal(true);  // Open modal FIRST so user sees response immediately
                fetchMealCategories();    // Then fetch in background
                fetchMeals();
              }}
              className="text-xs"
              data-testid="meals-btn"
            >
              <UtensilsCrossed className="w-3 h-3 mr-1" />
              Meal Ideas
            </Button>
            
            {/* Price Alerts button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchAlerts(); setShowAlertsModal(true); }}
              className="text-xs relative"
              data-testid="alerts-btn"
            >
              <Bell className="w-3 h-3 mr-1" />
              Alerts
              {priceAlerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                  {priceAlerts.length}
                </span>
              )}
            </Button>

            {savedRecipes.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSavedRecipesModal(true)}
                className="text-xs"
                data-testid="saved-recipes-btn"
              >
                <Heart className="w-3 h-3 mr-1 fill-red-500 text-red-500" />
                Saved ({savedRecipes.length})
              </Button>
            )}
          </div>
        </div>

        {/* Shopping List */}
        <div className="p-6" data-testid="shopping-list-container">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-syne text-lg font-bold text-primary">
              Your List ({activeItemsCount})
            </h2>
            <div className="flex items-center gap-2">
              {completedItemsCount > 0 && (
                <span className="text-sm text-muted-foreground">
                  {completedItemsCount} completed
                </span>
              )}
              {items.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShareLink(null);
                      setShowShareModal(true);
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                    data-testid="share-list-btn"
                  >
                    <Share2 className="w-3 h-3 mr-1" />
                    Share
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (window.confirm("Clear entire shopping list?")) {
                        try {
                          await apiClient.delete(`${API}/shopping-list`);
                          setItems([]);
                          setPriceResults(null);
                          toast.success("List cleared");
                        } catch (error) {
                          toast.error("Failed to clear list");
                        }
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="clear-list-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                </>
              )}
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-state">
              <div className="w-20 h-20 mx-auto mb-4 bg-secondary rounded-2xl flex items-center justify-center">
                <ShoppingCart className="w-10 h-10 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-manrope">
                Your shopping list is empty
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Add items above to start comparing prices
              </p>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="space-y-3">
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    data-testid={`list-item-${item.id}`}
                  >
                    <Card className={`group transition-all duration-200 hover:shadow-soft ${
                      item.completed ? 'bg-secondary/50 border-border' : 'hover:border-primary'
                    }`}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <button
                            onClick={() => handleToggleCompleted(item)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                              item.completed 
                                ? 'bg-green-500 border-green-500 text-white' 
                                : 'border-border hover:border-primary'
                            }`}
                            data-testid={`toggle-${item.id}`}
                          >
                            {item.completed && <Check className="w-4 h-4" />}
                          </button>
                          
                          <div className="min-w-0 flex-1">
                            <span className={`font-manrope font-medium block truncate ${
                              item.completed ? 'text-muted-foreground line-through' : 'text-primary'
                            }`}>
                              {item.name}
                            </span>
                            {item.barcode && (
                              <span className="text-xs text-muted-foreground font-space">
                                {item.barcode}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(item.id, item.name)}
                          className="opacity-50 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all flex-shrink-0"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          )}
        </div>

        {/* Basket Total Comparison */}
        {priceResults && priceResults.store_totals && priceResults.store_totals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-6 mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl"
            data-testid="basket-totals"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag className="w-5 h-5 text-green-600" />
              <h3 className="font-syne font-bold text-green-800">Total Basket Cost</h3>
            </div>
            
            <div className="space-y-2">
              {priceResults.store_totals.map((store, index) => {
                const storeStyle = getStoreStyle(store.store);
                const isCheapest = index === 0;
                const isOverBudget = weeklyBudget && store.total > weeklyBudget;
                const hasAllItems = (store.items_missing || 0) === 0;
                const completeStores = priceResults.store_totals.filter(s => (s.items_missing || 0) === 0);
                const isCheapestComplete = hasAllItems && completeStores.length > 0 && completeStores[0].store === store.store;
                
                return (
                  <div 
                    key={store.store}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      isCheapestComplete ? 'bg-green-100' : isCheapest && !hasAllItems ? 'bg-amber-50' : isOverBudget ? 'bg-red-50' : 'bg-white/50'
                    }`}
                  >
                    <div className="flex flex-col gap-0.5 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`${storeStyle.bg} ${storeStyle.text} text-xs`}>
                          {store.store}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {store.items_found}/{store.items_found + (store.items_missing || 0)} items found
                        </span>
                      </div>
                      {store.missing_item_names && store.missing_item_names.length > 0 && (
                        <p className="text-xs text-amber-600 ml-1">
                          ⚠️ Price unavailable: {store.missing_item_names.join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-space font-bold ${isCheapestComplete ? 'text-green-600 text-lg' : isOverBudget ? 'text-red-500' : 'text-primary'}`}>
                        £{store.total.toFixed(2)}
                      </span>
                      {isCheapestComplete && (
                        <Badge className="bg-green-500 text-white text-xs">Complete</Badge>
                      )}
                      {isCheapest && !isCheapestComplete && !hasAllItems && (
                        <Badge className="bg-amber-500 text-white text-xs">Best available</Badge>
                      )}
                      {isOverBudget && !isCheapest && (
                        <Badge className="bg-red-500 text-white text-xs">Over Budget</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Basket Split — recommends optimal 2-store shop when worth it */}
            {priceResults.optimal_split && (
              <div className="mt-4 p-4 bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">💡</span>
                  <h3 className="font-syne font-bold text-emerald-900">
                    {priceResults.optimal_split.framing === 'savings' ? (
                      <>Save £{priceResults.optimal_split.savings_vs_complete?.toFixed(2)} by splitting your shop</>
                    ) : (
                      <>Get all {priceResults.optimal_split.items_covered} items by splitting</>
                    )}
                  </h3>
                </div>

                <p className="text-xs text-emerald-700 mb-3">
                  {priceResults.optimal_split.framing === 'savings' ? (
                    <>vs. £{priceResults.store_totals.find(s => s.store === priceResults.optimal_split.complete_baseline_store)?.total.toFixed(2)} at {priceResults.optimal_split.complete_baseline_store} alone</>
                  ) : (
                    <>No single store has everything — combine 2 stores to cover your full list</>
                  )}
                </p>

                {/* Per-store breakdown */}
                <div className="space-y-2">
                  {priceResults.optimal_split.stores.map((storeName) => {
                    const storeStyle = getStoreStyle(storeName);
                    const itemsHere = priceResults.optimal_split.items_per_store[storeName] || [];
                    const subtotal = priceResults.optimal_split.store_subtotals[storeName] || 0;
                    return (
                      <div key={storeName} className="bg-white p-3 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Badge className={`${storeStyle.bg} ${storeStyle.text} text-xs`}>
                              {storeName}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Buy {itemsHere.length} item{itemsHere.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          <span className="font-space font-bold text-primary">
                            £{subtotal.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-1">
                          {itemsHere.join(", ")}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Total + missing items */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-emerald-200">
                  <span className="font-syne font-semibold text-emerald-900">Split total</span>
                  <span className="font-space font-bold text-lg text-emerald-700">
                    £{priceResults.optimal_split.total.toFixed(2)}
                  </span>
                </div>

                {priceResults.optimal_split.items_missing && priceResults.optimal_split.items_missing.length > 0 && (
                  <p className="text-xs text-amber-700 mt-2 ml-1">
                    ⚠️ Price unavailable at either store: {priceResults.optimal_split.items_missing.join(", ")}
                  </p>
                )}
              </div>
            )}

            {/* ── Step 5a: Savings card — shareable viral moment ─────────────────
                 Shown when user has a genuine split-shop saving.
                 Copy button generates a shareable text for WhatsApp/Reddit/social. */}
            {priceResults.optimal_split && priceResults.optimal_split.savings_vs_complete > 0 && (
              <div className="mt-3 p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-syne font-bold text-lg leading-tight">
                      🎉 You saved £{priceResults.optimal_split.savings_vs_complete.toFixed(2)} today!
                    </p>
                    <p className="text-emerald-100 text-xs mt-1">
                      vs shopping entirely at {priceResults.optimal_split.complete_baseline_store} (£{(priceResults.optimal_split.total + priceResults.optimal_split.savings_vs_complete).toFixed(2)})
                    </p>
                    <p className="text-emerald-100 text-xs mt-0.5">
                      Split: {priceResults.optimal_split.stores.join(' + ')}
                    </p>
                  </div>
                  <button
                    className="flex-shrink-0 flex items-center gap-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors whitespace-nowrap"
                    onClick={() => {
                      const storeNames = priceResults.optimal_split.stores.join(' + ');
                      const text = `I just saved £${priceResults.optimal_split.savings_vs_complete.toFixed(2)} on my weekly shop by splitting between ${storeNames} using PriceSlice 🛒\n\nFree UK supermarket price comparison → priceslice.uk`;
                      navigator.clipboard.writeText(text).then(() => {
                        toast.success('Copied! Share it with friends 🎉');
                        if (typeof window.gtag !== 'undefined') {
                          window.gtag('event', 'savings_card_shared', {
                            saving_amount: priceResults.optimal_split.savings_vs_complete.toFixed(2),
                            stores: storeNames
                          });
                        }
                      }).catch(() => {
                        toast.error('Could not copy — try manually');
                      });
                    }}
                  >
                    📋 Share
                  </button>
                </div>
              </div>
            )}

            {/* Budget Status */}
            {weeklyBudget && priceResults.cheapest_total && (
              <div className={`p-3 rounded-lg mt-3 ${
                priceResults.cheapest_total <= weeklyBudget 
                  ? 'bg-green-100 border border-green-200' 
                  : 'bg-red-100 border border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {priceResults.cheapest_total <= weeklyBudget ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">
                        Within budget! £{(weeklyBudget - priceResults.cheapest_total).toFixed(2)} remaining
                      </span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-700 font-medium">
                        £{(priceResults.cheapest_total - weeklyBudget).toFixed(2)} over your £{weeklyBudget} budget
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Savings message — only shown when the 'cheapest' store has full
                coverage. With partial baskets, the savings number is illusory
                because shopping at the cheaper-but-partial store leaves the
                user needing to find the missing items elsewhere. The split
                card above already gives proper apples-to-apples guidance for
                those cases. */}
            {priceResults.potential_savings > 0 && (() => {
              const cheapestStoreData = priceResults.store_totals?.find(
                s => s.store === priceResults.cheapest_store
              );
              const cheapestIsComplete = cheapestStoreData && (cheapestStoreData.items_missing || 0) === 0;
              return cheapestIsComplete ? (
                <p className="text-sm text-green-700 mt-3 font-medium">
                  You could save up to £{priceResults.potential_savings.toFixed(2)} by shopping at {priceResults.cheapest_store}!
                </p>
              ) : null;
            })()}
            
            <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-green-200/50">
              ⓘ Prices are estimates and may vary in-store. Product sizes may differ between stores — check pack size before buying. Always verify prices before purchasing.
            </p>
          </motion.div>
        )}

        {/* Price Comparison Results */}
        {priceResults && priceResults.item_results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 border-t border-border bg-secondary/30"
            data-testid="price-results"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-syne text-lg font-bold text-primary">
                Price Breakdown
              </h2>
              {/* Price Freshness Indicator */}
              {priceFreshness && (
                <Badge 
                  variant="secondary" 
                  className={`text-xs flex items-center gap-1 ${
                    priceFreshness.freshness_status === 'fresh' 
                      ? 'bg-green-100 text-green-700' 
                      : priceFreshness.freshness_status === 'stale'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Radio className={`w-3 h-3 ${priceFreshness.freshness_status === 'fresh' ? 'animate-pulse' : ''}`} />
                  {priceFreshness.freshness_status === 'fresh' && 'Recently updated'}
                  {priceFreshness.freshness_status === 'stale' && `Updated ${Math.round(priceFreshness.hours_since_update)}h ago`}
                  {priceFreshness.freshness_status === 'outdated' && 'Prices may be outdated'}
                  {priceFreshness.freshness_status === 'no_live_data' && 'Estimated prices'}
                </Badge>
              )}
              {!priceFreshness && (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs flex items-center gap-1">
                  <Radio className="w-3 h-3" />
                  Estimated prices
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Compared {priceResults.item_results.length} item(s) in {priceResults.comparison_time}
            </p>

            <div className="space-y-6">
              {priceResults.item_results.map((result, index) => (
                <div key={index} className="bg-white rounded-xl p-4 border border-border" data-testid={`price-result-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-syne font-bold text-primary capitalize">
                        {result.item}
                        {result.quantity > 1 && (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            ×{result.quantity}
                          </span>
                        )}
                      </h3>
                      {result.calories ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            {result.calories} cal
                            {result.quantity > 1 && result.total_calories && (
                              <span className="ml-1 font-semibold">→ {result.total_calories} total</span>
                            )}
                          </Badge>
                          {result.sugar && (
                            <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                              {result.sugar} sugar
                              {result.quantity > 1 && result.total_sugar && (
                                <span className="ml-1 font-semibold">→ {result.total_sugar} total</span>
                              )}
                            </Badge>
                          )}
                          {result.nutrition_per && (
                            <span className="text-xs text-muted-foreground">/{result.nutrition_per}</span>
                          )}
                        </div>
                      ) : null}
                    </div>
                    {result.barcode && (
                      <span className="text-xs text-muted-foreground font-space">
                        EAN: {result.barcode}
                      </span>
                    )}
                  </div>

                  {result.error && result.prices.length === 0 ? (
                    <p className="text-sm text-amber-600 italic">
                      {result.error}
                    </p>
                  ) : result.prices.length === 0 ? (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-sm text-muted-foreground italic">
                        Not in our database yet
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => {
                          setAlertType('grocery');
                          setAlertItemName(result.item || '');
                          setAlertTargetPrice('');
                          fetchAlerts();
                          setShowAlertsModal(true);
                        }}
                      >
                        <Bell className="w-3 h-3 mr-1" />
                        Notify me when found
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {result.prices
                        .sort((a, b) => a.price - b.price)
                        .slice(0, 6)
                        .map((price, priceIndex) => {
                          const storeStyle = getStoreStyle(price.store);
                          const isCheapest = result.cheapest && price.price === result.cheapest.price;
                          
                          return (
                            <div
                              key={priceIndex}
                              className={`flex items-center justify-between p-3 rounded-lg border overflow-hidden ${
                                isCheapest ? 'border-green-200 bg-green-50' : 'border-border'
                              }`}
                              data-testid={`price-item-${index}-${priceIndex}`}
                            >
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1" style={{maxWidth: '45%'}}>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <Badge className={`${storeStyle.bg} ${storeStyle.text} font-manrope flex-shrink-0 text-xs`}>
                                    {price.store}
                                  </Badge>
                                  {price.size && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {price.size}
                                    </span>
                                  )}
                                </div>
                                {price.is_alternative && price.alternative_product_name && (
                                  <span className="text-xs font-semibold text-foreground truncate">
                                    {price.alternative_product_name}
                                  </span>
                                )}
                                {(() => {
                                  const unitStr = formatUnitPrice(price);
                                  return unitStr && (
                                    <span className="text-xs text-muted-foreground">
                                      {unitStr}
                                    </span>
                                  );
                                })()}
                                {price.is_alternative && price.alternative_note && (
                                  <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-0.5 inline-block w-fit">
                                    {price.alternative_note}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                <div className="text-right">
                                  <span className={`font-space font-bold text-lg ${isCheapest ? 'text-green-600' : 'text-primary'}`}>
                                    £{price.price.toFixed(2)}
                                  </span>
                                </div>
                                {isCheapest && (
                                  <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                                    Cheapest
                                  </Badge>
                                )}
                                <a
                                  href={getAffiliateLink(price.store, result.item, result.barcode)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    trackAffiliateClick(price.store, result.item);
                                  }}
                                  className="ml-2"
                                  data-testid={`buy-btn-${index}-${priceIndex}`}
                                >
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs hover:bg-primary hover:text-white transition-colors"
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Buy
                                  </Button>
                                </a>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Compare Button */}
        {items.length > 0 && (
          <div className="sticky bottom-0 p-6 bg-gradient-to-t from-white via-white to-transparent">
            <Button
              onClick={handleComparePrices}
              disabled={comparing || activeItemsCount === 0}
              className={`w-full h-14 bg-primary hover:bg-primary/90 text-white font-syne font-bold text-lg rounded-2xl shadow-xl transition-all ${
                !comparing && usage.can_compare ? 'compare-btn-pulse' : ''
              }`}
              data-testid="compare-prices-btn"
            >
              {comparing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Comparing Prices...
                </>
              ) : !usage.can_compare ? (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  Upgrade to Compare
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Compare Prices ({activeItemsCount})
                </>
              )}
            </Button>

            {/* Step 1: Searches-left counter removed — comparisons are now unlimited */}
            {usage.is_payg && (
              <p className="text-center text-xs text-muted-foreground mt-2">
                {usage.comparisons_remaining} Trial Pack credits remaining
              </p>
            )}
            {!usage.is_pro && !usage.is_payg && (
              <p className="text-center text-xs text-muted-foreground mt-2">
                Free · {5 - (items.filter(i => !i.completed).length)} item slot{5 - (items.filter(i => !i.completed).length) === 1 ? '' : 's'} remaining · <span className="text-primary cursor-pointer underline" onClick={() => setShowUpgradeModal(true)}>Unlock 50 items</span>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Barcode Scanner Modal */}
      <Dialog open={showScannerModal} onOpenChange={(open) => {
        if (!open) stopScanner();
        setShowScannerModal(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Scan Barcode
            </DialogTitle>
            <DialogDescription>
              Point your camera at a product barcode
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div 
              id="barcode-scanner" 
              ref={scannerRef}
              className="w-full h-64 bg-black rounded-lg overflow-hidden"
            />
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Position the barcode within the frame
            </p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => setShowScannerModal(false)}
            className="mt-4"
          >
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      {/* Fuel Prices Modal */}
      <Dialog open={showFuelModal} onOpenChange={(open) => {
        setShowFuelModal(open);
        if (!open) {
          // Reset state when modal closes
          setPostcodeInput("");
          setPostcodeError("");
          setGeolocationFailed(false);
          setActiveFuelFilters([]);
        }
      }}>
        <DialogContent className="max-w-[360px] w-[calc(100vw-32px)] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              ⛽ Cheapest Fuel Near You
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>Prices from UK fuel stations near you · Updated daily</span>
              {fuelData && (
                <button
                  onClick={() => { setUserLocation(null); setLocationTimestamp(null); setUsingPostcode(false); setPostcodeInput(""); handleFuelPrices(); }}
                  className="text-xs text-orange-500 hover:text-orange-700 font-medium flex items-center gap-1 ml-2 shrink-0"
                >
                  <RefreshCw className="w-3 h-3" /> Use my location
                </button>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Postcode input - only show after results loaded OR if geolocation failed */}
          {(fuelData || geolocationFailed) && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] font-medium text-orange-800">
                  {geolocationFailed ? '📍 Enter your postcode' : '📍 Wrong location?'}
                </span>
                {usingPostcode && fuelData && (
                  <span className="text-[10px] text-orange-600 bg-orange-100 rounded px-1.5 py-0.5">Using postcode</span>
                )}
              </div>
              <form onSubmit={handlePostcodeSubmit} className="flex gap-1.5">
                <input
                  type="text"
                  value={postcodeInput}
                  onChange={(e) => { setPostcodeInput(e.target.value.toUpperCase()); setPostcodeError(""); }}
                  placeholder="Enter postcode (e.g. B44 8DG)"
                  className="flex-1 text-xs px-2 py-1.5 border border-orange-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                  disabled={loadingFuel}
                />
                <button
                  type="submit"
                  disabled={loadingFuel || !postcodeInput.trim()}
                  className="text-xs px-3 py-1.5 bg-orange-500 text-white rounded font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Search
                </button>
              </form>
              {postcodeError && (
                <p className="text-[10px] text-red-600 mt-1">{postcodeError}</p>
              )}
            </div>
          )}

          {loadingFuel ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              <p className="text-sm font-medium text-orange-600">{fuelLoadingStep}</p>
              <p className="text-xs text-muted-foreground">This may take a few seconds</p>
            </div>
          ) : fuelData ? (
            (() => {
              // Compute filtered stations and stats once per render
              const filteredStations = fuelData.stations?.filter(s =>
                activeFuelFilters.every(filter => s[filter] === true)
              ) || [];
              
              const hasFilters = activeFuelFilters.length > 0;
              
              // When filters active, use filtered stats; otherwise use overall stats from API
              const petrolPrices = filteredStations.map(s => s.petrol_price).filter(p => p);
              const dieselPrices = filteredStations.map(s => s.diesel_price).filter(p => p);
              
              const cheapestPetrol = hasFilters
                ? (petrolPrices.length ? Math.min(...petrolPrices) : null)
                : fuelData.cheapest_petrol;
              const cheapestDiesel = hasFilters
                ? (dieselPrices.length ? Math.min(...dieselPrices) : null)
                : fuelData.cheapest_diesel;
              const avgPetrol = hasFilters
                ? (petrolPrices.length ? petrolPrices.reduce((a, b) => a + b, 0) / petrolPrices.length : null)
                : fuelData.average_petrol;
              
              return (
            <div className="space-y-4">

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2 w-full">
                <div className="bg-green-50 rounded-lg p-2 border border-green-200 flex items-center justify-between min-w-0">
                  <p className="text-[10px] text-muted-foreground">{hasFilters ? 'Cheapest petrol' : 'Petrol'}</p>
                  <p className="font-space font-bold text-base text-green-600 ml-2">
                    {cheapestPetrol ? `${cheapestPetrol.toFixed(1)}p` : 'N/A'}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 border border-blue-200 flex items-center justify-between min-w-0">
                  <p className="text-[10px] text-muted-foreground">{hasFilters ? 'Cheapest diesel' : 'Diesel'}</p>
                  <p className="font-space font-bold text-base text-blue-600 ml-2">
                    {cheapestDiesel ? `${cheapestDiesel.toFixed(1)}p` : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Average prices */}
              <div className="bg-gray-50 rounded-lg p-2 flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  {hasFilters
                    ? `${filteredStations.length} of ${fuelData.stations?.length || 0} stations match`
                    : `${fuelData.stations?.length || 0} stations nearby`}
                </span>
                <span className="text-xs text-muted-foreground truncate">Avg: {avgPetrol?.toFixed(1) || 'N/A'}p</span>
              </div>

              {/* Amenity Filter Chips */}
              {fuelData.stations?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-muted-foreground">Filter by amenities:</span>
                    {activeFuelFilters.length > 0 && (
                      <button
                        onClick={() => setActiveFuelFilters([])}
                        className="text-[10px] text-orange-500 hover:text-orange-700 font-medium"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {[
                      { key: 'is_24_hour', label: '24-hour', icon: '⏰' },
                      { key: 'has_car_wash', label: 'Car wash', icon: '🚿' },
                      { key: 'has_adblue', label: 'AdBlue', icon: '💧' },
                      { key: 'has_lpg', label: 'LPG', icon: '🔥' },
                      { key: 'has_toilets', label: 'Toilets', icon: '🚻' },
                      { key: 'has_air_pump', label: 'Air pump', icon: '💨' },
                      { key: 'has_water', label: 'Water', icon: '🚰' },
                      { key: 'is_motorway', label: 'Motorway', icon: '🛣️' },
                      { key: 'is_supermarket', label: 'Supermarket', icon: '🛒' },
                    ].map(({ key, label, icon }) => {
                      const isActive = activeFuelFilters.includes(key);
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveFuelFilters(prev =>
                              prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
                            );
                          }}
                          className={`text-[11px] px-2 py-1 rounded-full border font-medium transition-colors ${
                            isActive
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300 hover:bg-orange-50'
                          }`}
                        >
                          {icon} {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Station List - filtered by selected amenities */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-syne font-bold text-sm">Nearby Stations</h4>
                  {activeFuelFilters.length > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      Showing {filteredStations.length} of {fuelData.stations?.length || 0}
                    </span>
                  )}
                </div>
                
                {filteredStations.length === 0 ? (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-800">
                      No stations match your filters.
                    </p>
                    <button
                      onClick={() => setActiveFuelFilters([])}
                      className="text-xs text-orange-600 hover:text-orange-800 font-medium mt-1"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  filteredStations.map((station, index) => {
                  // Prefer backend's distance_miles (calculated from actual user location).
                  // Use !== undefined check so 0 isn't treated as falsy.
                  const dist = (station.distance_miles !== undefined && station.distance_miles !== null)
                    ? station.distance_miles
                    : (userLocation && station.latitude && station.longitude
                        ? calcDistanceMiles(userLocation.lat, userLocation.lng, station.latitude, station.longitude)
                        : null);
                  return (
                    <div
                      key={station.site_id}
                      className={`rounded-xl p-3 border ${index === 0 ? 'border-green-300 bg-green-50' : 'border-border bg-white'}`}
                    >
                      {/* Top row: badges + price */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 flex-wrap">
                          {index === 0 && (
                            <Badge className="bg-green-500 text-white text-[10px] px-1.5 py-0">Cheapest</Badge>
                          )}
                          <Badge className={`text-[10px] px-1.5 py-0 ${getFuelTypeColor(station.retailer)}`}>
                            {station.brand || station.retailer}
                          </Badge>
                          {dist && (
                            <span className="text-[10px] font-medium text-primary">📍 {dist} mi away</span>
                          )}
                        </div>
                        <div className="text-right ml-2">
                          {station.petrol_price && (
                            <span className="font-space font-bold text-sm text-green-600">{station.petrol_price.toFixed(1)}p</span>
                          )}
                          {station.petrol_price && station.diesel_price && <span className="text-[10px] text-muted-foreground mx-1">/</span>}
                          {station.diesel_price && (
                            <span className="font-space text-[11px] text-blue-600">{station.diesel_price.toFixed(1)}p</span>
                          )}
                        </div>
                      </div>
                      {/* Address - full width below */}
                      {(() => {
                        const parts = (station.address || '').split(',');
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((station.address || '') + ' ' + (station.postcode || ''))}`;
                        return (
                          <div className="mt-1">
                            <p className="text-[11px] text-muted-foreground">{parts[0]?.trim()}</p>
                            {parts[1] && <p className="text-[11px] text-muted-foreground">{parts.slice(1).join(',').trim()}</p>}
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-[11px] text-muted-foreground font-medium">{station.postcode}</p>
                              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline">Directions</a>
                            </div>
                            {station.updated_at && (
                              <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                                Updated {new Date(station.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                            {(() => {
                              const loyalty = getLoyaltyScheme(station.brand || station.retailer);
                              return loyalty ? (
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  💳 <a href={loyalty.url} target="_blank" rel="noopener noreferrer" className="underline">
                                    {loyalty.scheme}
                                  </a>: {loyalty.rate}
                                </p>
                              ) : null;
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })
                )}
              </div>

              {/* Refresh button */}
              <Button
                onClick={handleFuelPrices}
                variant="outline"
                className="w-auto mx-auto block text-xs px-4"
                size="sm"
              >
                🔄 Refresh Prices
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Prices updated every 30 minutes · Data from UK Fuel Finder (Government Open Data)
              </p>
            </div>
              );
            })()
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      {/* ── Upgrade modal — Alerts tier (Step 10) ───────────────────────────── */}
      <Dialog open={showUpgradeModal} onOpenChange={(open) => {
        setShowUpgradeModal(open);
        if (!open) {
          setShowLicenseInput(false);
          setLicenseKey("");
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-2xl flex items-center gap-2">
              <Bell className="w-6 h-6 text-primary" />
              Unlock Alerts
            </DialogTitle>
            <DialogDescription>
              Bigger baskets, price alerts &amp; fuel notifications
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* License Key Input */}
            {showLicenseInput ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={licenseKey}
                    onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                    placeholder="PRICE-XXXX-XXXX-XXXX"
                    className="font-space text-center tracking-wider"
                    data-testid="license-key-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleActivateLicense}
                    disabled={activatingLicense || !licenseKey.trim()}
                    className="flex-1"
                    data-testid="activate-license-btn"
                  >
                    {activatingLicense ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Activate License
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowLicenseInput(false)}
                  >
                    Back
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  License can be used on up to 3 devices
                </p>
              </div>
            ) : (
              <>
                {/* Free vs Alerts comparison strip */}
                <div className="grid grid-cols-2 gap-2 p-3 bg-muted/40 rounded-xl text-xs">
                  <div>
                    <p className="font-syne font-bold text-muted-foreground mb-1.5">Free</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Unlimited comparisons</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />All 5 stores</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Barcode scanner</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Nutrition info</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Meal ideas</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Fuel prices</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Split-shop saving amount</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />1 grocery alert</li>
                      <li className="flex items-center gap-1.5 text-muted-foreground/60 line-through"><span className="w-3 h-3 shrink-0" />Up to 5 items only</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-syne font-bold text-primary mb-1.5">Alerts <span className="font-normal text-muted-foreground">£2.99/mo</span></p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 shrink-0" />Everything in Free</li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary shrink-0" /><span className="font-medium text-primary">Up to 50 items</span></li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary shrink-0" /><span className="font-medium text-primary">Full split-shop breakdown</span></li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary shrink-0" /><span className="font-medium text-primary">Unlimited grocery alerts</span></li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary shrink-0" /><span className="font-medium text-primary">Fuel price alerts ⛽</span></li>
                      <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary shrink-0" /><span className="font-medium text-primary">Loyalty card prices</span></li>
                    </ul>
                  </div>
                </div>

                {/* Alerts Monthly — PRIMARY */}
                <Card
                  className="cursor-pointer hover:border-primary transition-all border-primary bg-primary/5 relative"
                  onClick={() => handleUpgrade('alerts_monthly')}
                  data-testid="upgrade-alerts-monthly"
                >
                  <div className="absolute -top-2.5 left-4">
                    <Badge className="bg-primary text-white text-xs">Most popular</Badge>
                  </div>
                  <CardContent className="p-4 pt-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-syne font-bold text-lg">Alerts Monthly</h3>
                        <p className="text-sm text-muted-foreground">50 items · Price &amp; fuel alerts</p>
                      </div>
                      <div className="text-right">
                        <span className="font-space font-bold text-2xl">£2.99</span>
                        <span className="text-muted-foreground text-sm">/month</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* License Key card */}
                <Card
                  className="cursor-pointer hover:border-primary transition-all border-dashed"
                  onClick={() => setShowLicenseInput(true)}
                  data-testid="have-license-btn"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-primary" />
                        <div>
                          <h3 className="font-syne font-bold text-sm">Have a license key?</h3>
                          <p className="text-xs text-muted-foreground">Tap to activate your existing key</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>

                {/* Promo Code card — kept as-is per brief */}
                <Card
                  className="cursor-pointer hover:border-primary transition-all border-dashed bg-green-50/50 border-green-200"
                  onClick={() => setIsPromoModalOpen(true)}
                  data-testid="have-promo-code-btn"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-green-600" />
                        <div>
                          <h3 className="font-syne font-bold text-sm text-green-700">Have a promo code?</h3>
                          <p className="text-xs text-green-600">Tap to enter your code</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Promo Code Modal */}
          <PromoCodeModal
            isOpen={isPromoModalOpen}
            onClose={() => setIsPromoModalOpen(false)}
            onSuccess={handlePromoSuccess}
            deviceId={getDeviceId()}
            apiUrl={API}
          />
        </DialogContent>
      </Dialog>

      {/* PayG Info Modal */}
      <Dialog open={showPaygModal} onOpenChange={setShowPaygModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne text-2xl flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              Your Trial Pack
            </DialogTitle>
            <DialogDescription>
              Your current pay-as-you-go credits and usage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Credits remaining</span>
                <span className="font-space font-bold text-xl text-primary">{usage.comparisons_remaining}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Valid until</span>
                <span className="text-sm font-medium">
                  {usage.payg_expires_at
                    ? new Date(usage.payg_expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '30 days from purchase'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((usage.comparisons_remaining || 0) / 15) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">{usage.comparisons_remaining} of 15 credits remaining</p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 font-medium">⚠️ Credits expire 30 days from purchase — unused credits are forfeited</p>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>✅ Each grocery comparison uses 1 credit</p>
              <p>⛽ Fuel searches are free — unlimited</p>
              <p>🍽️ All 15 meal categories unlocked</p>
            </div>
            <Button
              className="w-full"
              onClick={() => { setShowPaygModal(false); setShowUpgradeModal(true); }}
            >
              <Bell className="w-4 h-4 mr-2" />
              Upgrade to Alerts for Unlimited Access
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Subscription Modal */}
      <Dialog open={showManageModal} onOpenChange={setShowManageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne text-2xl flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              Manage Subscription
            </DialogTitle>
            <DialogDescription>
              Manage your PriceSlice Pro subscription, update payment details, or cancel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">PriceSlice Pro — Active</p>
                  <p className="text-sm text-green-600">Unlimited comparisons + fuel searches</p>
                </div>
              </div>
            </div>
            {isProViaCode ? (
              // Promo code users — no Stripe billing portal
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <p className="text-sm text-amber-800 font-medium">Promo code access</p>
                <p className="text-xs text-amber-700 mt-1">
                  Your access was granted via a promo code — there's no billing to manage.
                  Your code expires on {proUntilDate ? new Date(proUntilDate).toLocaleDateString('en-GB') : 'your set date'}.
                </p>
              </div>
            ) : (
              <>
                {/* Cancellation pending warning banner */}
                {usage.subscription_cancel_at && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">⚠️</span>
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Subscription cancelling</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Your Pro access ends on {new Date(usage.subscription_cancel_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}. You can resubscribe any time before then.
                      </p>
                    </div>
                  </div>
                )}
                <Button
                  className="w-full"
                  onClick={async () => {
                    setLoadingPortal(true);
                    try {
                      const response = await apiClient.post(`${API}/billing/portal`, {
                        return_url: window.location.href
                      });
                      window.location.href = response.data.url;
                    } catch (error) {
                      const detail = error.response?.data?.detail || "Could not open billing portal. Please try again.";
                      toast.error(detail);
                    } finally {
                      setLoadingPortal(false);
                    }
                  }}
                  disabled={loadingPortal}
                >
                  {loadingPortal ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening portal...</>
                  ) : (
                    usage.subscription_cancel_at ? 'Resubscribe or Update Billing' : 'Manage Billing & Cancel Subscription'
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  You'll be redirected to Stripe's secure billing portal
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* License Key Success Modal */}
      <Dialog open={!!showLicenseSuccess} onOpenChange={() => setShowLicenseSuccess(null)}>
        <DialogContent className="max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="font-syne text-2xl flex items-center justify-center gap-2">
              <Check className="w-8 h-8 text-green-500" />
              Payment Successful!
            </DialogTitle>
            <DialogDescription className="mt-2">
              Save your license key to use PriceSlice Pro on other devices
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">Your License Key:</p>
            <p className="font-space font-bold text-xl tracking-wider" data-testid="license-key-display">
              {showLicenseSuccess}
            </p>
          </div>
          
          <div className="mt-4 space-y-2">
            <Button
              onClick={() => {
                navigator.clipboard.writeText(showLicenseSuccess);
                toast.success("License key copied to clipboard!");
              }}
              className="w-full"
              data-testid="copy-license-btn"
            >
              Copy License Key
            </Button>
            <p className="text-xs text-muted-foreground">
              This key can be used on up to 3 devices. Keep it safe!
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowLicenseSuccess(null)}
            className="mt-4"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Budget Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Set Weekly Budget
            </DialogTitle>
            <DialogDescription>
              We'll show you if your basket is within budget
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">£</span>
              <Input
                type="number"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="50.00"
                className="text-lg font-space"
                data-testid="budget-input"
              />
            </div>
            
            <div className="flex gap-2">
              {[30, 50, 75, 100].map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setBudgetInput(amount.toString())}
                  className="flex-1"
                >
                  £{amount}
                </Button>
              ))}
            </div>
            
            <Button 
              onClick={handleSaveBudget}
              className="w-full"
              data-testid="save-budget-btn"
            >
              Save Budget
            </Button>
            
            {weeklyBudget && (
              <Button 
                variant="outline"
                onClick={() => {
                  setWeeklyBudget(null);
                  setShowBudgetModal(false);
                  toast.success("Budget removed");
                }}
                className="w-full text-red-500 hover:text-red-600"
              >
                Remove Budget
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Meal Suggestions Modal */}
      <Dialog open={showMealsModal} onOpenChange={setShowMealsModal}>
        <DialogContent className="max-w-lg w-[calc(100vw-24px)] max-h-[85vh] overflow-y-auto overflow-x-hidden p-4">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5 text-primary" />
              Meal Ideas
            </DialogTitle>
            <DialogDescription className="text-xs">
              {meals.length} meals found {selectedCategory ? `in ${selectedCategory.replace("_", " ")}` : ''} {weeklyBudget ? `under £${weeklyBudget}` : ''}
              {!usage.is_pro && !usage.is_payg && <span className="ml-1 text-amber-600">(Free: 3 categories · <button className="underline" onClick={() => setShowUpgradeModal(true)}>Unlock all</button>)</span>}
            </DialogDescription>
          </DialogHeader>

          {/* Full modal loader — shown on first open while categories + meals fetch */}
          {(loadingMealCategories || (loadingMeals && mealCategories.length === 0)) ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Finding meal ideas...</p>
            </div>
          ) : (
          <div className="space-y-4 mt-2">
            {/* Category Filter */}
            <div className="grid grid-cols-2 gap-1 w-full overflow-hidden">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setSelectedCategory(null);
                  fetchMeals(null);
                  if (mealsListRef.current) mealsListRef.current.scrollTop = 0;
                }}
                className="text-xs w-full min-w-0 px-2 col-span-2"
              >
                All ({meals.length})
              </Button>
              {mealCategories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (!cat.available) {
                      toast.info("Upgrade to Alerts (£2.99/mo) to unlock all cuisines!");
                      setShowProModal(true);
                      return;
                    }
                    setSelectedCategory(cat.id);
                    fetchMeals(cat.id);
                    if (mealsListRef.current) mealsListRef.current.scrollTop = 0;
                  }}
                  className={`text-xs w-full min-w-0 px-2 ${!cat.available ? 'opacity-60' : ''}`}
                  disabled={!cat.available}
                  title={`${cat.name} (${cat.count})`}
                >
                  <span className="flex items-center gap-1 w-full min-w-0">
                    {!cat.available && <Lock className="w-3 h-3 flex-shrink-0" />}
                    <span className="truncate">{cat.name} ({cat.count})</span>
                  </span>
                </Button>
              ))}
            </div>
            
            {/* Meals List */}
            {loadingMeals ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : meals.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No meals found. Try adjusting your budget or category filter.
              </p>
            ) : (
              <div ref={mealsListRef} className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                {meals.map((meal, index) => (
                  <Card key={index} className="overflow-hidden hover:border-primary transition-colors">
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Meal Image */}
                        {meal.image && (
                          <div className="w-24 h-24 flex-shrink-0">
                            <img 
                              src={meal.image} 
                              alt={meal.name}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                        <div className="flex-1 p-3 min-w-0">
                          {/* Top row: name + action buttons */}
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <h3 className="font-syne font-bold text-primary text-sm leading-tight">{meal.name}</h3>
                                {/* Vegetarian indicator */}
                                {(meal.category === 'vegetarian' || meal.is_vegetarian) && (
                                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-xs font-bold flex-shrink-0" title="Vegetarian">
                                    V
                                  </span>
                                )}
                                {meal.category && (
                                  <Badge variant="outline" className="text-xs capitalize flex-shrink-0">
                                    {meal.category.replace("_", " ")}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Serves {meal.serves} · {meal.prep_time}
                              </p>
                            </div>
                            {/* Action buttons — flex-shrink-0 ensures they never get squeezed */}
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSaveRecipe(meal.name)}
                                className={`p-1.5 h-7 w-7 ${isRecipeSaved(meal.name) ? 'text-red-500' : 'text-muted-foreground'}`}
                                data-testid={`save-meal-${index}`}
                                title="Save to favourites"
                              >
                                <Heart className={`w-4 h-4 ${isRecipeSaved(meal.name) ? 'fill-current' : ''}`} />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleAddMealToList(meal.name)}
                                className="p-1.5 h-7 w-7"
                                data-testid={`add-meal-${index}`}
                                title="Add to shopping list"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {/* Badges row */}
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-xs">
                              £{meal.estimated_cost.toFixed(2)} total
                            </Badge>
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              £{meal.per_person.toFixed(2)}/person
                            </Badge>
                            {meal.calories && (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                🔥 {meal.calories} cal
                              </Badge>
                            )}
                            {meal.protein && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                💪 {meal.protein}g protein
                              </Badge>
                            )}
                            {meal.fibre && (meal.category === 'gym' || meal.category === 'healthy') && (
                              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                🌾 {meal.fibre}g fibre
                              </Badge>
                            )}
                            {meal.sugar && meal.category !== 'gym' && (
                              <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                                {meal.sugar} sugar
                              </Badge>
                            )}
                            {meal.subcategory && meal.category === 'gym' && (
                              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                                {meal.subcategory === 'pre_workout' ? '⚡ Pre-workout' :
                                 meal.subcategory === 'post_workout' ? '🔄 Recovery' :
                                 meal.subcategory === 'high_protein' ? '🥩 High protein' :
                                 meal.subcategory === 'bulking' ? '📈 Bulking' : meal.subcategory}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            <span className="font-medium">Ingredients:</span> {meal.ingredients.join(", ")}
                          </p>
                          {/* Cooking Instructions */}
                          {meal.instructions && meal.instructions.length > 0 && (
                            <details className="mt-1.5">
                              <summary className="text-xs font-medium text-primary cursor-pointer hover:underline">
                                ▶ How to cook ({meal.instructions.length} steps)
                              </summary>
                              <ol className="text-xs text-muted-foreground mt-1.5 space-y-1 list-decimal list-inside pl-1">
                                {meal.instructions.map((step, stepIndex) => (
                                  <li key={stepIndex}>{step}</li>
                                ))}
                              </ol>
                            </details>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          )} {/* end of loadingMealCategories conditional */}
        </DialogContent>
      </Dialog>

      {/* Price Alerts Modal */}
      {/* ── Price Alerts Modal ─────────────────────────────────────────────── */}
      <Dialog open={showAlertsModal} onOpenChange={setShowAlertsModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Price Alerts
            </DialogTitle>
            <DialogDescription>
              {alertsMeta.is_paid
                ? `Grocery: ${alertsMeta.grocery_count}/10 · Fuel: ${alertsMeta.fuel_count} (unlimited)`
                : "Free: 1 grocery alert · Upgrade for fuel alerts + 10 grocery alerts"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">

            {/* Alert type tabs */}
            <div className="flex rounded-lg bg-muted p-1 gap-1">
              <button
                className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${alertType === 'grocery' ? 'bg-white shadow text-primary' : 'text-muted-foreground'}`}
                onClick={() => setAlertType('grocery')}
              >
                🛒 Grocery
              </button>
              <button
                className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${alertsMeta.is_paid ? (alertType === 'fuel' ? 'bg-white shadow text-primary' : 'text-muted-foreground') : 'text-muted-foreground opacity-50 cursor-not-allowed'}`}
                onClick={() => {
                  if (!alertsMeta.is_paid) {
                    setShowAlertsModal(false);
                    setShowUpgradeModal(true);
                  } else {
                    setAlertType('fuel');
                  }
                }}
              >
                ⛽ Fuel {!alertsMeta.is_paid ? <span className="text-xs ml-1">🔒</span> : <span className="text-xs ml-1 text-green-600">∞</span>}
              </button>
            </div>

            {/* Grocery alert form */}
            {alertType === 'grocery' && (
              <div className="space-y-2">
                {alertsMeta.is_paid && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Grocery alerts</span>
                    <span className={alertsMeta.grocery_count >= 10 ? "text-red-500 font-medium" : "text-green-600 font-medium"}>
                      {alertsMeta.grocery_count}/10 used
                    </span>
                  </div>
                )}
                {!alertsMeta.can_add_grocery && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    {alertsMeta.is_paid
                      ? `You've used all 10 grocery alerts. Delete one to add another.`
                      : <>Free accounts get 1 grocery alert.{" "}
                          <button className="underline font-medium" onClick={() => { setShowAlertsModal(false); setShowUpgradeModal(true); }}>
                            Upgrade to Alerts (£2.99/mo)
                          </button>{" "}
                          for 10 grocery alerts + unlimited fuel alerts.</>
                    }
                  </div>
                )}
                <div className="relative flex items-center gap-2">
                  <Input
                    placeholder="Item name (e.g., cheddar cheese)"
                    value={alertItemName}
                    onChange={(e) => setAlertItemName(e.target.value)}
                    disabled={!alertsMeta.can_add_grocery}
                    data-testid="alert-item-input"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 px-2"
                    disabled={!alertsMeta.can_add_grocery}
                    title="Scan barcode"
                    onClick={() => {
                      setShowScannerModal(true);
                      // When scanner returns, populate alertItemName
                      window._alertScanCallback = (name) => {
                        setAlertItemName(name);
                        setShowScannerModal(false);
                      };
                    }}
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1 flex-1">
                    <span className="text-sm text-muted-foreground">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Target price (optional)"
                      value={alertTargetPrice}
                      onChange={(e) => setAlertTargetPrice(e.target.value)}
                      disabled={!alertsMeta.can_add_grocery}
                      data-testid="alert-price-input"
                    />
                  </div>
                  <select
                    className="border rounded-md px-2 text-sm bg-background"
                    value={alertStoreName}
                    onChange={(e) => setAlertStoreName(e.target.value)}
                    disabled={!alertsMeta.can_add_grocery}
                  >
                    <option value="">Any store</option>
                    <option value="Tesco">Tesco</option>
                    <option value="Sainsbury's">Sainsbury's</option>
                    <option value="Asda">Asda</option>
                    <option value="Morrisons">Morrisons</option>
                    <option value="Aldi">Aldi</option>
                  </select>
                </div>
                <p className="text-xs text-muted-foreground -mt-1">
                  Leave price blank to get notified on any drop from today's cheapest price
                </p>
                <Button
                  className="w-full"
                  onClick={handleCreateAlert}
                  disabled={!alertsMeta.can_add_grocery || isSubmittingAlert}
                  data-testid="create-alert-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {isSubmittingAlert ? "Setting up..." : "Set Grocery Alert"}
                </Button>
              </div>
            )}

            {/* Fuel alert form — paid only */}
            {alertType === 'fuel' && alertsMeta.is_paid && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Fuel type</label>
                    <select
                      className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                      value={alertFuelType}
                      onChange={(e) => setAlertFuelType(e.target.value)}
                    >
                      <option value="petrol">Petrol (E5)</option>
                      <option value="diesel">Diesel (B7)</option>
                      <option value="super">Super Unleaded</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Radius</label>
                    <select
                      className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                      value={alertFuelRadius}
                      onChange={(e) => setAlertFuelRadius(parseInt(e.target.value))}
                    >
                      <option value={2}>2 miles</option>
                      <option value={5}>5 miles</option>
                      <option value={10}>10 miles</option>
                      <option value={20}>20 miles</option>
                    </select>
                  </div>
                </div>
                <Input
                  placeholder="Your postcode (e.g. SW1A 1AA)"
                  value={alertFuelPostcode}
                  onChange={(e) => setAlertFuelPostcode(e.target.value.toUpperCase())}
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Target price in pence (e.g. 135)"
                    value={alertFuelTargetPence}
                    onChange={(e) => setAlertFuelTargetPence(e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">pence/litre</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  You'll be notified when {alertFuelType} drops below {alertFuelTargetPence || "?"}p within {alertFuelRadius} miles of {alertFuelPostcode || "your postcode"}.
                </p>
                <Button className="w-full" onClick={handleCreateAlert} disabled={isSubmittingAlert}>
                  <Bell className="w-4 h-4 mr-2" />
                  {isSubmittingAlert ? "Setting up..." : "Set Fuel Alert"}
                </Button>
              </div>
            )}

            {/* Active alerts list */}
            <div className="space-y-2 pt-2 border-t">
              <h4 className="font-syne font-bold text-sm flex items-center justify-between">
                Your Active Alerts
                <span className="text-xs text-muted-foreground font-normal">{priceAlerts.length} total</span>
              </h4>

              {priceAlerts.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-3">
                  No alerts yet — add one above
                </p>
              ) : (
                priceAlerts.map((alert) => (
                  <div key={alert.id}
                    className={`flex items-start justify-between p-3 border rounded-lg gap-2 ${alert.triggered ? 'bg-muted/20 border-dashed opacity-80' : ''} ${alert.match_status === 'not_found' ? 'bg-red-50 border-red-200' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm">{alert.alert_type === 'fuel' ? '⛽' : '🛒'}</span>
                        <p className={`font-medium text-sm capitalize truncate ${alert.triggered || alert.match_status === 'not_found' ? 'line-through text-muted-foreground' : ''}`}>
                          {alert.item_name}
                        </p>
                        {alert.triggered && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                            ✓ Alerted
                          </span>
                        )}
                        {alert.match_status === 'not_found' && (
                          <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                            Not found
                          </span>
                        )}
                        {!alert.triggered && alert.store_name && (
                          <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{alert.store_name}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                        <Target className="w-3 h-3 shrink-0" />
                        <span className={alert.triggered ? 'line-through' : ''}>
                          {alert.match_status === 'pending'
                            ? "Checking stores now\u2014we'll keep trying over the next few hours"
                            : alert.match_status === 'not_found'
                            ? "Not stocked anywhere we checked \u2014 delete and try a more general search"
                            : (alert.target_price === null || alert.target_price === undefined || isNaN(parseFloat(alert.target_price)))
                            ? 'Setting up\u2026'
                            : alert.alert_type === 'fuel'
                            ? `Below ${Math.round(parseFloat(alert.target_price) * 100)}p/litre`
                            : alert.baseline_mode
                            ? `Any drop below £${parseFloat(alert.target_price).toFixed(2)} (today's price)`
                            : `Below £${parseFloat(alert.target_price).toFixed(2)}`}
                        </span>
                        {alert.triggered && alert.current_price && (
                          <span className="text-green-600 font-medium">
                            · Got £{parseFloat(alert.current_price).toFixed(2)} on {new Date(alert.last_triggered_at).toLocaleDateString('en-GB')}
                          </span>
                        )}
                        {!alert.triggered && alert.email && (
                          <span className="text-blue-500">· 📧 {alert.email}</span>
                        )}
                        {!alert.triggered && alert.last_triggered_at && (
                          <span className="text-green-600">· Last triggered {new Date(alert.last_triggered_at).toLocaleDateString('en-GB', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="text-red-400 hover:text-red-600 p-1 shrink-0"
                      title="Delete alert"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center pb-1">
              Grocery alerts checked after every price update · Fuel alerts checked twice daily
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Saved Recipes Modal */}
      <Dialog open={showSavedRecipesModal} onOpenChange={setShowSavedRecipesModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              Saved Recipes
            </DialogTitle>
            <DialogDescription>
              Your favourite recipes ({savedRecipes.length} saved)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {savedRecipes.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No saved recipes yet. Tap the heart icon on any meal to save it.
              </p>
            ) : (
              savedRecipes.map((recipe, index) => (
                <Card key={index} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col">
                    <div className="flex">
                      {(recipe.image_url || recipe.image) && (
                        <div className="w-24 h-24 flex-shrink-0">
                          <img
                            src={recipe.image_url || recipe.image}
                            alt={recipe.recipe_name || recipe.name}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = "none"}
                          />
                        </div>
                      )}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-syne font-bold text-primary">{recipe.recipe_name || recipe.name}</h3>
                              <Badge variant="outline" className="text-xs capitalize">
                                {(recipe.category || "").replace("_", " ")}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Serves {recipe.servings || recipe.serves || "?"}{recipe.prep_time ? ` · ${recipe.prep_time}` : ""}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                £{(recipe.estimated_cost || 0).toFixed(2)} total
                              </Badge>
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                £{(recipe.per_person || (recipe.servings && recipe.estimated_cost ? recipe.estimated_cost / recipe.servings : 0)).toFixed(2)}/person
                              </Badge>
                              {recipe.calories && (
                                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                  🔥 {recipe.calories} cal
                                </Badge>
                              )}
                              {recipe.protein && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  💪 {recipe.protein} protein
                                </Badge>
                              )}
                              {recipe.fibre && (
                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                  🌾 {recipe.fibre} fibre
                                </Badge>
                              )}
                              {recipe.sugar && recipe.category !== 'gym' && (
                                <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                                  {recipe.sugar} sugar
                                </Badge>
                              )}
                              {recipe.subcategory && recipe.category === 'gym' && (
                                <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                                  {recipe.subcategory === 'pre_workout' ? '⚡ Pre-workout' :
                                   recipe.subcategory === 'post_workout' ? '🔄 Recovery' :
                                   recipe.subcategory === 'high_protein' ? '🥩 High protein' :
                                   recipe.subcategory === 'bulking' ? '📈 Bulking' : recipe.subcategory}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              {(() => { const ing = typeof recipe.ingredients === "string" ? JSON.parse(recipe.ingredients) : recipe.ingredients; return Array.isArray(ing) ? ing.join(", ") : ing; })()}
                            </p>
                            {(() => {
                              const steps = recipe.instructions
                                ? (typeof recipe.instructions === "string" ? JSON.parse(recipe.instructions) : recipe.instructions)
                                : [];
                              return steps.length > 0 ? (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-primary mb-1">
                                    How to cook ({steps.length} steps)
                                  </p>
                                  {steps.map((step, stepIndex) => (
                                    <div key={stepIndex} className="flex gap-2 mb-1">
                                      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">{stepIndex + 1}</span>
                                      <p className="text-xs text-muted-foreground">{step}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex flex-col gap-1 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveSavedRecipe(recipe.recipe_name || recipe.name)}
                              className="p-2 text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                handleAddMealToList(recipe.recipe_name || recipe.name);
                                setShowSavedRecipesModal(false);
                              }}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>{/* end inner flex row */}
                    </div>{/* end flex-col */}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share List Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share Shopping List
            </DialogTitle>
            <DialogDescription>
              Create a link to share your list with family or friends
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {!shareLink ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Anyone with the link can view and import your shopping list items.
                  The link expires in 7 days.
                </p>
                <Button
                  onClick={handleCreateShareLink}
                  disabled={creatingShareLink}
                  className="w-full"
                >
                  {creatingShareLink ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4 mr-2" />
                  )}
                  Generate Share Link
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    value={shareLink}
                    readOnly
                    className="flex-1 text-sm"
                  />
                  <Button onClick={copyShareLink} variant="outline" size="icon">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Share this link via WhatsApp, email, or any messaging app.
                  Valid for 7 days.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my shopping list: ${shareLink}`)}`, '_blank');
                    }}
                  >
                    Share via WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      window.open(`mailto:?subject=My Shopping List&body=${encodeURIComponent(`Check out my shopping list: ${shareLink}`)}`, '_blank');
                    }}
                  >
                    Share via Email
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="font-manrope">Confirming your payment...</p>
          </Card>
        </div>
      )}
    </div>
  );
}

export default App;